import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card } from '@consta/uikit/Card';
import { Text } from '@consta/uikit/Text';
import { Table } from '@consta/uikit/Table';
import { TextField } from '@consta/uikit/TextField';
import { Button } from '@consta/uikit/Button';
import { Switch } from '@consta/uikit/Switch';
import { DatePicker } from '@consta/uikit/DatePicker';
import { Checkbox } from '@consta/uikit/Checkbox';
import { Select } from '@consta/uikit/Select';
import { Combobox } from '@consta/uikit/Combobox';
import { IconTrash } from '@consta/icons/IconTrash';
import classes from './style/MonitoringStages.module.scss';
import { PostCreateWithLinksRequest, Project } from '../../../../../services/types/TProjectServices';
import {
  useLazyGetProjectCurrentStageInfoQuery,
  usePatchCreateWithLinksMutation,
  usePatchProjectMutation,
  usePatchSetCompletedMutation
} from '../../../../../services/projectService';
import { useLazyGetAreaQuery } from '../../../../../services/areaService';
import { useLazyGetDepositQuery } from '../../../../../services/depositService';
import { useLazyGetFieldQuery } from '../../../../../services/fieldService';
import { useLazyGetCompanyQuery } from '../../../../../services/companyService';
import { useLazyGetWellCategoryQuery } from '../../../../../services/miniportService';
import { useLazyGetWellQuery, usePostCreateWithLinksMutation, useLazyGetWellCategorySelectorQuery, useDeleteWithLinksMutation, usePatchWellMutation, useWellDensityImportMutation, usePostBulkWellMutation } from '../../../../../services/wellService';
import { useLazyGetStudiedPhaseQuery } from '../../../../../services/studiedPhaseService';
import { useLazyGetResearchObjectiveQuery } from '../../../../../services/researchObjectiveServices';
import { useLazyGetLayerQuery } from '../../../../../services/layerService';
import { useLazyGetHorizonQuery } from '../../../../../services/horizonService';
import LoadDataModal from '../../../../Modals/LoadData/LoadData';
import { useDispatch, useSelector } from 'react-redux';
import { setProjectData, updateProjectAreas, updateProjectBasicInfo, updateProjectCompany, updateProjectDeposits, updateProjectFields, updateProjectStage } from '../../../../../store/authSlice';
import { useLazyGetByProjectQuery, usePostReportMutation } from '../../../../../services/reportService';
import { Modal } from '@consta/uikit/Modal/index';
import { IconExpand } from '@consta/icons/IconExpand'
import { IconAdd } from '@consta/icons/IconAdd/index';
import { Loader } from '@consta/uikit/Loader'
import { createPortal } from 'react-dom';
import { IconUpload } from '@consta/icons/IconUpload';
import { IconClose } from '@consta/icons/IconClose';
import { Pagination } from '@consta/uikit/Pagination/index';

interface MonitoringStagesProps {
  userAccessLevel: number | null;
  projectData?: Project;
  projectUuid?: string | any;
  navigate: (path: string) => void;
  onOpenModal: (modalName: string) => void;
}

const MonitoringStages = ({ userAccessLevel, projectData, projectUuid, navigate, onOpenModal }: MonitoringStagesProps) => {
  const path = window.location.pathname;

  const renderStage = () => {
    switch (path) {
      case `/platform/project/${projectUuid}/monitoring`:
        return <Stage1 navigate={navigate} projectData={projectData} projectUuid={projectUuid} />;
      case `/platform/project/${projectUuid}/monitoring/2`:
        return <Stage2 navigate={navigate} projectUuid={projectUuid} onOpenModal={onOpenModal} />;
      case `/platform/project/${projectUuid}/monitoring/3`:
        return <Stage3 navigate={navigate} projectUuid={projectUuid} onOpenModal={onOpenModal} />;
      case `/platform/project/${projectUuid}/monitoring/4`:
        return <Stage4 navigate={navigate} projectUuid={projectUuid} onOpenModal={onOpenModal} />;
      case `/platform/project/${projectUuid}/monitoring/5`:
        return <Stage5 navigate={navigate} projectUuid={projectUuid} onOpenModal={onOpenModal} />;
      default:
        return null;
    }
  };

  return (
    <div className={classes.monitoring}>
      {renderStage()}
    </div>
  );
};

// Stage 1: Основные сведения
const Stage1 = ({ navigate, projectData, projectUuid }: any) => {
  const dispatch = useDispatch();
  const projectDataFromStore = useSelector((state: any) => state.auth.projectData);

  const [value, setValue] = useState(null);

  // Функция для форматирования даты
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString);
    } catch {
      return null;
    }
  };

  // Получение данных для этапа 1
  const [getStage1Data, { data: stage1Data, isLoading: isLoadingStage1 }] = useLazyGetProjectCurrentStageInfoQuery();

  // Получение компаний для комбобокса заказчика
  const [getCompanies, { data: companiesData, isLoading: isLoadingCompanies }] = useLazyGetCompanyQuery();

  // Получение месторождений для комбобокса
  const [getFields, { data: fieldsData, isLoading: isLoadingFields }] = useLazyGetFieldQuery();

  // Получение залежей для комбобокса
  const [getDeposits, { data: depositsData, isLoading: isLoadingDeposits }] = useLazyGetDepositQuery();

  // Получение участков для комбобокса
  const [getAreas, { data: areasData, isLoading: isLoadingAreas }] = useLazyGetAreaQuery();

  // Мутация для сохранения с связями
  const [postCreateWithLinks, { isLoading: isSaving }] = usePatchCreateWithLinksMutation();

  // Мутация для обновления проекта
  const [patchProject, { isLoading: isUpdatingStage }] = usePatchProjectMutation();

  // Состояния для выбранных значений
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedFields, setSelectedFields] = useState<any[]>([]);
  const [selectedDeposits, setSelectedDeposits] = useState<any[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<any[]>([]);

  // Состояния для текстовых полей
  const [projectNumber, setProjectNumber] = useState<any>('');
  const [projectName, setProjectName] = useState<any>('');
  const [projectDescription, setProjectDescription] = useState<any>('');
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);

  // Текущий UUID компании для фильтрации
  const [currentCompanyUuid, setCurrentCompanyUuid] = useState<string | any>('');

  // Референс для отслеживания инициализации данных из API
  const isDataInitializedRef = useRef(false);

  useEffect(() => {
    if (projectUuid) {
      //('🚀 Загрузка данных Stage1 для проекта:', projectUuid);
      getStage1Data({
        uuid: projectUuid,
        current_stage: 1
      });

      // Загружаем компании
      getCompanies({
        limit: 100,
        offset: 0,
        ordering: 'name'
      });
    }
  }, [projectUuid, getStage1Data, getCompanies]);

  // Когда изменился currentCompanyUuid, загружаем месторождения
  useEffect(() => {
    if (currentCompanyUuid) {
      //('🛢️ Загрузка месторождений для компании:', currentCompanyUuid);
      getFields({
        limit: 100,
        offset: 0,
        ordering: 'name',
        companies: currentCompanyUuid,
      });
    } else {
      // Если компания не выбрана, очищаем месторождения
      setSelectedFields([]);
    }
  }, [currentCompanyUuid, getFields]);

  // Когда выбраны месторождения, загружаем залежи с фильтрацией по компаниям и выбранным месторождениям
  useEffect(() => {
    if (currentCompanyUuid && selectedFields?.length > 0) {
      const fieldUuids: any = selectedFields.map(field => field.uuid);
      //('⛽ Загрузка залежей для месторождений:', fieldUuids);

      getDeposits({
        limit: 100,
        offset: 0,
        ordering: 'name',
        field: fieldUuids.join(','), // Передаем строку с UUID через запятую
        companies: currentCompanyUuid // Передаем строку UUID компании
      });
    } else {
      // Если нет компании или месторождений, очищаем залежи
      setSelectedDeposits([]);
    }
  }, [currentCompanyUuid, selectedFields, getDeposits]);

  // Когда выбраны залежи, загружаем участки с фильтрацией по компаниям и выбранным залежам
  useEffect(() => {
    if (currentCompanyUuid && selectedDeposits?.length > 0) {
      const depositUuids: any = selectedDeposits.map(deposit => deposit.uuid);
      //('🗺️ Загрузка участков для залежей:', depositUuids);

      getAreas({
        limit: 100,
        offset: 0,
        ordering: 'name',
        deposits: depositUuids.join(','), // Передаем строку с UUID через запятую
        companies: currentCompanyUuid, // Передаем строку UUID компании
        unassigned: true,
      });
    } else {
      // Если нет компании или залежей, очищаем участки
      setSelectedAreas([]);
    }
  }, [currentCompanyUuid, selectedDeposits, getAreas]);

  // ОБНОВЛЯЕМ СОСТОЯНИЯ ПРИ ПОЛУЧЕНИИ ДАННЫХ ИЗ API
  useEffect(() => {
    if (stage1Data && !isDataInitializedRef.current) {
      //('🔄 Обновление состояний из API данных:', stage1Data);

      // Устанавливаем текстовые поля
      setProjectNumber(stage1Data?.number || '');
      setProjectName(stage1Data?.name || '');
      setProjectDescription(stage1Data?.description || '');
      setStartDate(formatDate(stage1Data?.start_date || ''));
      setEndDate(formatDate(stage1Data?.end_date || ''));

      // Устанавливаем компанию из stage1Data
      if (stage1Data.company) {
        const companyFromData = {
          label: stage1Data.company.name,
          //@ts-ignore
          id: stage1Data.company.uuid,
          ...stage1Data.company
        };
        setSelectedCompany(companyFromData);
        setCurrentCompanyUuid(stage1Data.company.uuid);
        //('🏢 Установлена компания из API:', companyFromData.label);
      }

      // Устанавливаем месторождения из stage1Data.fields если есть
      if (stage1Data.fields && stage1Data.fields?.length > 0) {
        const fieldsFromData = stage1Data.fields.map((field: any) => ({
          label: field.name,
          id: field.uuid,
          ...field
        }));
        setSelectedFields(fieldsFromData);
        //('🛢️ Установлены месторождения из API:', fieldsFromData.length);
      }

      // Устанавливаем залежи из stage1Data.deposits если есть
      if (stage1Data.deposits && stage1Data.deposits?.length > 0) {
        const depositsFromData = stage1Data.deposits.map((deposit: any) => ({
          label: deposit.name,
          id: deposit.uuid,
          ...deposit
        }));
        setSelectedDeposits(depositsFromData);
        //('⛽ Установлены залежи из API:', depositsFromData.length);
      }

      // Устанавливаем участки из stage1Data.areas если есть
      if (stage1Data.areas && stage1Data.areas?.length > 0) {
        const areasFromData = stage1Data.areas.map((area: any) => ({
          label: area.name,
          id: area.uuid,
          ...area
        }));
        setSelectedAreas(areasFromData);
        //('🗺️ Установлены участки из API:', areasFromData.length);
      }

      // Сохраняем данные в Redux
      const projectDataToStore: any = {
        uuid: stage1Data.uuid,
        name: stage1Data.name,
        description: stage1Data.description,
        number: stage1Data.number,
        start_date: stage1Data.start_date,
        end_date: stage1Data?.end_date,
        company: stage1Data.company,
        fields: stage1Data.fields || [],
        deposits: stage1Data.deposits || [],
        areas: stage1Data.areas || [],
        is_completed: stage1Data.is_completed,
        current_stage: stage1Data.current_stage
      };

      dispatch(setProjectData(projectDataToStore));
      //('💾 Данные сохранены в Redux');

      isDataInitializedRef.current = true;
    }
  }, [stage1Data, dispatch]);

  // Обработчик изменения компании
  const handleCompanyChange = (company: any) => {
    //('🏢 Изменение компании:', company?.label);
    setSelectedCompany(company);
    setCurrentCompanyUuid(company?.uuid || '');

    // Сбрасываем зависимые поля при смене компании
    setSelectedFields([]);
    setSelectedDeposits([]);
    setSelectedAreas([]);

    // Обновляем в Redux
    if (company) {
      dispatch(updateProjectCompany({
        uuid: company.uuid,
        name: company.label
      }));
    } else {
      dispatch(updateProjectCompany({
        uuid: '',
        name: ''
      }));
    }
  };

  // Обработчик изменения месторождений
  const handleFieldsChange: any = (fields: any[]) => {
    //('🛢️ Изменение месторождений:', fields?.map(f => f.label));
    setSelectedFields(fields || []);

    // Обновляем в Redux
    dispatch(updateProjectFields(
      fields?.map(field => ({
        uuid: field.id,
        name: field.label
      })) || []
    ));
  };

  // Обработчик изменения залежей
  const handleDepositsChange: any = (deposits: any[]) => {
    //('⛽ Изменение залежей:', deposits?.map(d => d.label));
    setSelectedDeposits(deposits || []);

    // Обновляем в Redux
    dispatch(updateProjectDeposits(
      deposits?.map(deposit => ({
        uuid: deposit.id,
        name: deposit.label,
        field: deposit.field
      })) || []
    ));
  };

  // Обработчик изменения участков
  const handleAreasChange: any = (areas: any[]) => {
    //('🗺️ Изменение участков:', areas?.map(a => a.label));
    setSelectedAreas(areas || []);

    // Обновляем в Redux
    dispatch(updateProjectAreas(
      areas?.map(area => ({
        uuid: area.id,
        name: area.label,
        deposit: area.deposit
      })) || []
    ));
  };

  // Функция для сохранения данных
  const handleSave = async () => {
    try {
      const requestData: PostCreateWithLinksRequest = {
        project_uuid: projectUuid,
        name: projectName,
        description: projectDescription,
        number: projectNumber,
        start_date: startDate ? startDate.toISOString().split('T')[0] : '',
        end_date: endDate ? endDate?.toISOString().split('T')[0] : '',
        is_completed: false,
        company: selectedCompany?.uuid || '',
        field_ids: selectedFields?.map(field => field.uuid) || [],
        deposit_ids: selectedDeposits?.map(deposit => deposit.uuid) || [],
        area_ids: selectedAreas?.map(area => area.uuid) || [],
      };

      //('💾 Сохранение данных:', requestData);
      await postCreateWithLinks(requestData).unwrap();

      // Сохраняем основные данные в Redux
      dispatch(updateProjectBasicInfo({
        name: projectName,
        description: projectDescription,
        number: projectNumber,
        start_date: startDate ? startDate.toISOString().split('T')[0] : '',
        end_date: endDate ? endDate?.toISOString().split('T')[0] : '',
      }));

      //('✅ Данные успешно сохранены');

    } catch (error) {
      console.error('❌ Ошибка при сохранении:', error);
    }
  };

  // Функция для перехода на следующий этап с обновлением current_stage
  const handleOpenNextStage = async () => {
    try {
      // Сначала сохраняем основные данные
      await handleSave();

      // Затем обновляем current_stage проекта
      if (projectUuid) {
        await patchProject({
          uuid: projectUuid,
          current_stage: 2,
          name: projectName,
          description: projectDescription,
          number: projectNumber,
          start_date: startDate ? startDate.toISOString().split('T')[0] : '',
          end_date: endDate ? endDate?.toISOString().split('T')[0] : '',
          is_completed: false,
          company: selectedCompany?.uuid || '',
          company_name: selectedCompany?.label || ''
        }).unwrap();

        // Обновляем этап в Redux
        dispatch(updateProjectStage({
          current_stage: 2
        }));

        //('✅ Этап проекта обновлен на 2');
      }

      // Переходим на следующий этап
      navigate(`/project/${projectUuid}/monitoring/2`);

    } catch (error) {
      console.error('❌ Ошибка при переходе на следующий этап:', error);
    }
  };

  // Преобразование данных для комбобоксов - ВСЕГДА ИЗ ЗАПРОСОВ
  const companyItems = companiesData?.results?.map((company: any) => ({
    label: company.name,
    id: company.uuid,
    ...company
  })) || [];

  const fieldItems = fieldsData?.results?.map((field: any) => ({
    label: field.name,
    id: field.uuid,
    ...field
  })) || [];

  const depositItems = depositsData?.results?.map((deposit: any) => ({
    label: deposit.name,
    id: deposit.uuid,
    ...deposit
  })) || [];

  const areaItems = areasData?.results?.map((area: any) => ({
    label: area.name,
    id: area.uuid,
    ...area
  })) || [];

  return (
    <>
      <div className={classes.header}>
        <Text size='2xl' weight='bold'>Мониторинг проекта - Этап 1</Text>
        <Text size='s' view='secondary'>Информация о проекте</Text>
        {isLoadingStage1 && <Text size='s' view='secondary'>Загрузка данных...</Text>}
      </div>

      <div className={classes.cardMonitoring}>
        <Card className={classes.card}>
          <Text>Основные сведения</Text>
          <div className={classes.topCard}>
            <TextField
              className={classes.stage1TextField}
              label='Номер проекта'
              placeholder='Введите номер'
              value={projectNumber}
              onChange={(value) => setProjectNumber(value || '')}
            />
            <TextField
              className={classes.stage1TextField}
              label='Наименование проекта'
              placeholder='Введите наименование'
              value={projectName}
              onChange={(value) => setProjectName(value || '')}
            />
          </div>
          <TextField
            label='Описание'
            placeholder='Введите описание проекта'
            value={projectDescription}
            onChange={(value) => setProjectDescription(value || '')}
          />
          <div className={classes.middleCard}>
            <div className={classes.left}>
              <Combobox
                style={{ width: '32vw' }}
                label='Заказчик'
                items={companyItems}
                value={selectedCompany}
                onChange={handleCompanyChange}
                isLoading={isLoadingCompanies}
                placeholder="Выберите заказчика"
              />
              <DatePicker
                label='Дата завершения'
                value={endDate || null}
                onChange={setEndDate}
              />
              <Combobox
                label='Месторождения'
                items={fieldItems}
                value={selectedFields}
                onChange={handleFieldsChange}
                multiple
                isLoading={isLoadingFields}
                placeholder="Выберите месторождения"
                disabled={!currentCompanyUuid}
              />
            </div>
            <div className={classes.right}>
              <DatePicker
                style={{ width: '32vw' }}
                label='Дата начала'
                value={startDate}
                onChange={setStartDate}
              />
              <Combobox
                label='Залежи'
                items={depositItems}
                value={selectedDeposits}
                onChange={handleDepositsChange}
                multiple
                isLoading={isLoadingDeposits}
                placeholder="Выберите залежи"
                disabled={!currentCompanyUuid || selectedFields?.length === 0}
              />
              <Combobox
                label='Участки'
                items={areaItems}
                value={selectedAreas}
                onChange={handleAreasChange}
                multiple
                isLoading={isLoadingAreas}
                placeholder="Выберите участки"
                disabled={!currentCompanyUuid || selectedDeposits?.length === 0}
              />
            </div>
          </div>
          <div className={classes.bottomCard}>
            <Button
              label='Сохранить'
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving}
            />
            <Button
              label='Открыть'
              style={{ backgroundColor: 'green' }}
              onClick={handleOpenNextStage}
              loading={isUpdatingStage}
              disabled={isSaving || isUpdatingStage}
            />
          </div>
        </Card>
      </div>
    </>
  );
};


// Добавим в начале файла, после импортов, хук для автосохранения
const useAutoSaveOnInactivity = (
  onSave: () => Promise<void>,
  inactivityTimeout: number = 300000,
  dependencies: any[] = []
) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isSavingRef = useRef<boolean>(false);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(async () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity >= inactivityTimeout && !isSavingRef.current) {
        try {
          isSavingRef.current = true;
          await onSave();
        } catch (error) {
          console.error(' Ошибка автосохранения:', error);
        } finally {
          isSavingRef.current = false;
        }
      }
    }, inactivityTimeout);
  }, [inactivityTimeout, onSave]);

  useEffect(() => {
    // Отслеживаем события пользовательской активности
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'input', 'change'];

    const handleActivity = () => {
      resetTimer();
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Начальный запуск таймера
    resetTimer();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [resetTimer, ...dependencies]);

  // Возвращаем функцию для ручного сброса таймера
  return { resetTimer };
};

// Stage 2: Работа со скважинами
const Stage2 = ({ navigate, projectUuid, onOpenModal }: any) => {
  // Получение данных для этапа 2
  const [getStage2Data, { data: stage2Data, isLoading: isLoadingStage2 }] = useLazyGetProjectCurrentStageInfoQuery();

  // Получение данных первой стадии для участков
  const [getStage1Data, { data: stage1Data, isLoading: isLoadingStage1 }] = useLazyGetProjectCurrentStageInfoQuery();

  // Получение данных скважин
  const [triggerGetWell, { data: wellData, isLoading: isLoadingWells, error: wellError }] = useLazyGetWellQuery();

  // Получение категорий скважин
  const [triggerGetWellCategory, { data: wellCategoryData, isLoading: isLoadingCategories }] = useLazyGetWellCategorySelectorQuery();

  // Получение горизонтов (шифр горизонта)
  const [getHorizons, { data: horizonsData, isLoading: isLoadingHorizons }] = useLazyGetHorizonQuery();

  // Получение пластов (название пласта)
  const [getLayers, { data: layersData, isLoading: isLoadingLayers }] = useLazyGetLayerQuery();

  // Получение целей исследования
  const [getResearchObjectives, { data: researchObjectivesData, isLoading: isLoadingResearchObjectives }] = useLazyGetResearchObjectiveQuery();

  // Получение исследуемых фаз
  const [getStudiedPhases, { data: studiedPhasesData, isLoading: isLoadingStudiedPhases }] = useLazyGetStudiedPhaseQuery();

  // Мутация для массового сохранения скважин
  const [postBulkWell, { isLoading: isSavingBulk }] = usePostBulkWellMutation();

  // Мутация для обновления скважины
  const [patchWell, { isLoading: isPatching }] = usePatchWellMutation();

  // Мутация для удаления скважин со связями
  const [deleteWithLinks, { isLoading: isDeleting }] = useDeleteWithLinksMutation();

  // Мутация для обновления проекта
  const [patchProject, { isLoading: isUpdatingStage }] = usePatchProjectMutation();

  // Состояния
  const [editableRows, setEditableRows] = useState<any[]>([]);
  const [horizonCache, setHorizonCache] = useState<Record<string, any[]>>({});
  const [layerCache, setLayerCache] = useState<Record<string, any[]>>({});
  const [areaItems, setAreaItems] = useState<any[]>([]);
  const [categoryItems, setCategoryItems] = useState<any[]>([]);
  const [researchObjectiveItems, setResearchObjectiveItems] = useState<any[]>([]);
  const [studiedPhaseItems, setStudiedPhaseItems] = useState<any[]>([]);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [allHorizons, setAllHorizons] = useState<any[]>([]);
  const [allLayers, setAllLayers] = useState<any[]>([]);
  const [isLoadDataModalOpen, setIsLoadDataModalOpen] = useState(false);
  const [justAddedWell, setJustAddedWell] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Функция для получения данных текущей страницы
  const getPaginatedData = (data: any[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  // Добавляем индекс к данным для отображения номера строки
  const addIndexToData = (data: any[], page: number) => {
    return data.map((item, idx) => ({
      ...item,
      rowIndex: (page - 1) * itemsPerPage + idx + 1
    }));
  };

  // Получаем данные для отображения с пагинацией
  const paginatedRows = addIndexToData(getPaginatedData(editableRows, currentPage), currentPage);
  const totalPages = Math.ceil(editableRows.length / itemsPerPage);

  // Обработчик смены страницы
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Сбрасываем страницу при изменении данных
  useEffect(() => {
    if (justAddedWell) {
      const totalPages = Math.ceil(editableRows.length / itemsPerPage);
      setCurrentPage(totalPages);
      setJustAddedWell(false);
    }
  }, [editableRows.length, justAddedWell]);

  // Добавляем состояние для модального окна
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);

  // Референсы для отслеживания инициализации
  const isInitializedRef = useRef(false);
  const pendingUpdatesRef = useRef<Map<string, any>>(new Map());
  const initializationInProgressRef = useRef(false);

  // Используем хук автосохранения
  const autoSave = useAutoSaveOnInactivity(
    async () => {
      if (!projectUuid || editableRows.length === 0 || isSavingBulk || isPatching) {
        return;
      }

      try {
        await handleSave();
      } catch (error) {
        setTimeout(() => {
        }, 3000);
      }
    },
    300000,
    [projectUuid, editableRows, isSavingBulk, isPatching]
  );

  // Инициализация данных
  useEffect(() => {
    if (projectUuid) {
      const initializeData = async () => {
        try {
          await Promise.all([
            getStage2Data({ uuid: projectUuid, current_stage: 2, limit: 9999 }),
            getStage1Data({ uuid: projectUuid, current_stage: 1, limit: 9999 }),
            triggerGetWell({ project: projectUuid, limit: 9999 }),
            triggerGetWellCategory({}),
            getResearchObjectives({}),
            getStudiedPhases({})
          ]);
        } catch (error) {
          console.error('❌ Ошибка при инициализации данных:', error);
        }
      };
      initializeData();
    }
  }, [projectUuid]);

  // Загрузка всех горизонтов и пластов при инициализации
  useEffect(() => {
    if (projectUuid) {
      const horizonParams: any = {
        limit: 9999,
        offset: 0,
        ordering: 'code',
        project: [projectUuid]
      };
      getHorizons(horizonParams);

      const layerParams: any = {
        limit: 9999,
        offset: 0,
        ordering: 'name',
        project: [projectUuid]
      };
      getLayers(layerParams);
    }
  }, [projectUuid]);

  // Сохранение всех горизонтов
  useEffect(() => {
    if (horizonsData?.results) {
      const horizons = horizonsData.results.map(horizon => ({
        label: horizon.code || 'Неизвестный горизонт',
        id: horizon.uuid || horizon.id,
        description: horizon.description
      }));
      setAllHorizons(horizons);
    }
  }, [horizonsData]);

  // Сохранение всех пластов
  useEffect(() => {
    if (layersData?.results) {
      const layers = layersData.results.map(layer => ({
        label: layer.name || 'Неизвестный пласт',
        id: layer.uuid || layer.id,
        horizon: layer.horizon,
        code: layer.code
      }));
      setAllLayers(layers);
    }
  }, [layersData]);

  // Обработка участков
  useEffect(() => {
    if (stage1Data?.areas) {
      let areas: any = [];
      if (Array.isArray(stage1Data.areas)) {
        areas = stage1Data.areas.map((area: any) => ({
          label: area.name || 'Неизвестный участок',
          id: area.uuid || area.id
        }));
      } else if (stage1Data.areas && typeof stage1Data.areas === 'object') {
        areas = [{
          label: stage1Data.areas.name || 'Неизвестный участок',
          id: stage1Data.areas.uuid || stage1Data.areas.id
        }];
      }
      setAreaItems(areas);
    }
  }, [stage1Data]);

  // Обработка категорий
  useEffect(() => {
    if (wellCategoryData && Array.isArray(wellCategoryData)) {
      const categories = wellCategoryData.map(category => ({
        label: category.name || 'Неизвестная категория',
        id: category.uuid || category.id
      }));
      setCategoryItems(categories);
    }
  }, [wellCategoryData]);

  // Обработка целей исследования
  useEffect(() => {
    if (researchObjectivesData && Array.isArray(researchObjectivesData)) {
      const objectives = researchObjectivesData.map(objective => ({
        label: objective.name || 'Неизвестная цель',
        id: objective.uuid || objective.id
      }));
      setResearchObjectiveItems(objectives);
    }
  }, [researchObjectivesData]);

  // Обработка исследуемых фаз
  useEffect(() => {
    if (studiedPhasesData && Array.isArray(studiedPhasesData)) {
      const phases = studiedPhasesData.map(phase => ({
        label: phase.name || 'Неизвестная фаза',
        id: phase.uuid || phase.id
      }));
      setStudiedPhaseItems(phases);
    }
  }, [studiedPhasesData]);

  // Функция для безопасного обновления строки
  const updateRowSafely = useCallback((rowId: string, updates: any) => {
    setEditableRows(prevRows =>
      prevRows.map(row =>
        row.id === rowId ? { ...row, ...updates } : row
      )
    );
    autoSave.resetTimer();
  }, [autoSave.resetTimer]);

  // Функция для загрузки горизонтов с кешированием
  const loadHorizonsByArea = async (areaUuid: string): Promise<any[]> => {
    if (!projectUuid) return allHorizons;

    if (!areaUuid) {
      return allHorizons;
    }

    const cacheKey = `${projectUuid}-${areaUuid}`;
    if (horizonCache[cacheKey]) {
      return horizonCache[cacheKey];
    }

    try {
      const params = {
        limit: 100,
        offset: 0,
        ordering: 'code',
        project: [projectUuid],
        areas: [areaUuid]
      };

      const { data } = await getHorizons(params);

      if (data?.results) {
        const horizons = data.results.map(horizon => ({
          label: horizon.code || 'Неизвестный горизонт',
          id: horizon.uuid || horizon.id
        }));

        setHorizonCache(prev => ({ ...prev, [cacheKey]: horizons }));
        return horizons;
      }
      return allHorizons;
    } catch (error) {
      console.error('❌ Ошибка при загрузке горизонтов, возвращаем все:', error);
      return allHorizons;
    }
  };

  // Функция для загрузки пластов с точной фильтрацией по горизонту
  const loadLayersByHorizon = async (horizonUuid: string): Promise<any[]> => {
    if (!projectUuid || !horizonUuid) {
      return [];
    }

    const cacheKey = `layers-${projectUuid}-${horizonUuid}`;

    if (layerCache[cacheKey]) {
      return layerCache[cacheKey];
    }

    try {
      const params = {
        limit: 100,
        offset: 0,
        ordering: 'name',
        project: [projectUuid],
        horizons: [horizonUuid]
      };

      const { data } = await getLayers(params);

      if (data?.results) {
        const layers = data.results.map(layer => ({
          label: layer.name || 'Неизвестный пласт',
          id: layer.uuid || layer.id,
          horizon: layer.horizon
        }));

        setLayerCache(prev => ({
          ...prev,
          [cacheKey]: layers
        }));

        return layers;
      }

      return [];
    } catch (error) {
      console.error('❌ Ошибка при загрузке пластов:', error);
      return [];
    }
  };

  // Функция для точного сопоставления горизонтов и пластов из API
  const extractHorizonsAndLayers = (well: any) => {
    const horizonsItems: any[] = [];
    let horizonsText = '';

    const layersItems: any[] = [];
    let layersText = '';

    if (well.horizons_info && Array.isArray(well.horizons_info)) {
      well.horizons_info.forEach((horizon: any) => {
        const foundHorizon = allHorizons.find(item => item.id === horizon.uuid);

        if (foundHorizon) {
          horizonsItems.push(foundHorizon);
        } else {
          const tempHorizon = {
            label: horizon.code || 'Неизвестный горизонт',
            id: horizon.uuid,
            description: horizon.description
          };
          horizonsItems.push(tempHorizon);
        }
      });

      horizonsText = horizonsItems.map(h => h.label).join(', ');
    }

    if (well.layers_info && Array.isArray(well.layers_info)) {
      well.layers_info.forEach((layer: any) => {
        const foundLayer = allLayers.find(item => item.id === layer.uuid);

        if (foundLayer) {
          layersItems.push(foundLayer);
        } else {
          const tempLayer = {
            label: layer.name || 'Неизвестный пласт',
            id: layer.uuid,
            horizon: layer.code
          };
          layersItems.push(tempLayer);
        }
      });

      layersText = layersItems.map(l => l.label).join(', ');
    }

    return {
      horizonsItems,
      horizonsText,
      layersItems,
      layersText
    };
  };

  // Функция для инициализации доступных данных строки
  const initializeRowData = async (row: any): Promise<any> => {
    try {
      let availableHorizons: any[] = [];
      let availableLayers: any[] = [];

      if (row.areaName?.id) {
        availableHorizons = await loadHorizonsByArea(row.areaName.id);
      }

      if (row.horizonsInfo && row.horizonsInfo.length > 0) {
        const horizonLayerPromises = row.horizonsInfo.map(async (horizon: any) => {
          if (horizon?.id) {
            return await loadLayersByHorizon(horizon.id);
          }
          return [];
        });

        const layersResults = await Promise.allSettled(horizonLayerPromises);

        layersResults.forEach(result => {
          if (result.status === 'fulfilled') {
            availableLayers.push(...result.value);
          }
        });

        availableLayers = availableLayers.filter((layer, index, self) =>
          index === self.findIndex(l => l.id === layer.id)
        );
      }

      return {
        availableHorizons,
        availableLayers,
        isLoadingHorizons: false,
        isLoadingLayers: false
      };

    } catch (error) {
      console.error(`❌ Ошибка инициализации данных для строки ${row.id}:`, error);
      return {
        availableHorizons: [],
        availableLayers: [],
        isLoadingHorizons: false,
        isLoadingLayers: false
      };
    }
  };

  // Полная инициализация строк таблицы
  useEffect(() => {
    if (wellData?.results && Array.isArray(wellData.results) &&
      areaItems.length > 0 && categoryItems.length > 0 &&
      studiedPhaseItems.length > 0 && researchObjectiveItems.length > 0 &&
      !isInitializedRef.current) {

      isInitializedRef.current = true;
      initializationInProgressRef.current = true;

      const initializeRows = async () => {
        try {
          const initialRows = wellData.results.map((well, index) => {
            const rowId = well.uuid || `well-${well.id}` || `index-${index}`;

            const pendingUpdate = pendingUpdatesRef.current.get(rowId);
            if (pendingUpdate) {
              pendingUpdatesRef.current.delete(rowId);
              return pendingUpdate;
            }

            const findItem = (items: any[], uuid: string) =>
              uuid ? items.find(item => item.id === uuid) || null : null;
            
            const findItemByName = (items: any[], name: string) =>
              name ? items.find(item => item.label === name) || null : null;

            const areaItem = findItem(areaItems, well.area);
            const categoryItem = findItem(categoryItems, well.category);
            const studiedPhaseItem = findItemByName(studiedPhaseItems, well.studied_phase);
            const researchObjectiveItem = findItemByName(researchObjectiveItems, well.research_objective);

            const { horizonsItems, horizonsText, layersItems, layersText } = extractHorizonsAndLayers(well);

            const rowData = {
              id: rowId,
              uuid: well.uuid,

              wellNumber: well.number?.toString() || '',
              workshop: well.workshop?.toString() || '',
              brigade: well.team?.toString() || '',
              qLiquid: well.liquid_rate?.toString() || '',
              qOil: well.oil_rate?.toString() || '',
              liquidRate: well.liquid_rate?.toString() || '',
              waterCut: well.water_cut?.toString() || '',
              xCoord: well.x_coords?.toString() || '',
              yCoord: well.y_coords?.toString() || '',
              note: well.note || '',
              sampleNote: well.sample_note || '',
              oilVolume: well.oil_volume?.toString() || '',
              waterVolume: well.water_volume?.toString() || '',
              waterDensity: well.water_density?.toString() || '',
              waterEc: well.water_ec?.toString() || '',

              areaName: areaItem,
              areaNameText: areaItem?.label || well.area_name || '',
              categoryName: categoryItem,
              categoryNameText: categoryItem?.label || well.category_name || '',

              horizonsInfo: horizonsItems,
              horizonsInfoText: horizonsText,
              layersInfo: layersItems,
              layersInfoText: layersText,

              studiedPhase: studiedPhaseItem,
              studiedPhaseText: studiedPhaseItem?.label || well.studied_phase_name || '',
              researchObjective: researchObjectiveItem,
              researchObjectiveText: researchObjectiveItem?.label || well.research_objective_name || '',

              isSampleTaken: well.is_sample_taken || false,
              isOilSampleReceived: well.is_oil_sample_received || false,
              isOilSplit: well.is_oil_split || false,
              isWaterSampleReceived: well.is_water_sample_received || false,
              isWaterSplit: well.is_water_split || false,
              isGh: well.is_gh || false,
              isIcp: well.is_icp || false,
              isHav: well.is_hav || false,
              isKapel: well.is_kapel || false,

              isLoadingHorizons: false,
              isLoadingLayers: false,
              availableHorizons: [],
              availableLayers: [],
              isDeleting: false
            };

            return rowData;
          });

          const initializationPromises = initialRows.map(async (row) => {
            const data = await initializeRowData(row);
            return { rowId: row.id, data };
          });

          const initializationResults = await Promise.allSettled(initializationPromises);

          const resultsMap = new Map();
          initializationResults.forEach(result => {
            if (result.status === 'fulfilled') {
              resultsMap.set(result.value.rowId, result.value.data);
            }
          });

          const finalRows = initialRows.map(row => {
            const rowData = resultsMap.get(row.id);
            if (rowData) {
              return {
                ...row,
                availableHorizons: rowData.availableHorizons,
                availableLayers: rowData.availableLayers
              };
            }
            return row;
          });

          setEditableRows(finalRows);

        } catch (error) {
          console.error('❌ Ошибка при инициализации строк:', error);
          isInitializedRef.current = false;
        } finally {
          initializationInProgressRef.current = false;
        }
      };

      initializeRows();
    }
  }, [wellData, areaItems, categoryItems, studiedPhaseItems, researchObjectiveItems]);

  // Обработчик изменения текстовых полей
  const handleTextFieldChange = (rowId: string, fieldName: string) =>
    (value: string | null) => {
      updateRowSafely(rowId, { [fieldName]: value || '' });
    };

  // Обработчик изменения селектов
  const handleSelectChange = (rowId: string, fieldName: string) =>
    (value: any) => {
      const updates = {
        [fieldName]: value,
        [`${fieldName}Text`]: value?.label || ''
      };
      updateRowSafely(rowId, updates);
    };

  // Обработчик удаления строки с подтверждением и API запросом
  const handleDeleteRow = async (rowId: string) => {
    const rowToDelete = editableRows.find(row => row.id === rowId);
    if (!rowToDelete) {
      console.error('❌ Строка для удаления не найдена:', rowId);
      return;
    }

    if (!rowToDelete.uuid) {
      setEditableRows(prevRows => prevRows.filter(row => row.id !== rowId));
      return;
    }

    const isConfirmed = window.confirm(
      `Вы уверены, что хотите удалить скважину №${rowToDelete.wellNumber}?`
    );

    if (!isConfirmed) {
      return;
    }

    try {
      await deleteWithLinks({ uuid: rowToDelete.uuid }).unwrap();
      setEditableRows(prevRows => prevRows.filter(row => row.id !== rowId));
    } catch (error) {
      console.error('❌ Ошибка при удалении скважины:', error);
    }
  };

  // Обработчик удаления с обработкой состояний загрузки
  const handleDeleteWithLoading = (rowId: string) => {
    const rowToDelete = editableRows.find(row => row.id === rowId);
    if (!rowToDelete) return;

    updateRowSafely(rowId, { isDeleting: true });

    handleDeleteRow(rowId).finally(() => {
      updateRowSafely(rowId, { isDeleting: false });
    });
  };

  // Обработчик для формирования мини-отчета
  const handleGenerateMiniReport = async () => {
    if (!projectUuid) return;

    setIsGeneratingReport(true);

    try {
      await triggerGetWellCategory({ project_uuid: projectUuid }).unwrap();
      onOpenModal('stage2');
    } catch (err) {
      console.error('❌ Ошибка при формировании мини-отчета:', err);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Функция для проверки, изменилась ли скважина
  const hasWellChanged = (originalWell: any, currentWell: any): boolean => {
    // Сравниваем только изменяемые поля
    const fieldsToCompare = [
      'wellNumber', 'workshop', 'brigade', 'qLiquid', 'qOil', 
      'liquidRate', 'waterCut', 'xCoord', 'yCoord', 'note',
      'isSampleTaken', 'sampleNote', 'isOilSampleReceived', 'oilVolume',
      'isOilSplit', 'isWaterSampleReceived', 'waterVolume', 'isWaterSplit',
      'waterDensity', 'waterEc', 'isGh', 'isIcp', 'isHav', 'isKapel'
    ];

    for (const field of fieldsToCompare) {
      if (originalWell[field] !== currentWell[field]) {
        return true;
      }
    }

    // Проверяем связи
    if (originalWell.areaName?.id !== currentWell.areaName?.id) return true;
    if (originalWell.categoryName?.id !== currentWell.categoryName?.id) return true;
    if (originalWell.studiedPhase?.id !== currentWell.studiedPhase?.id) return true;
    if (originalWell.researchObjective?.id !== currentWell.researchObjective?.id) return true;
    
    // Проверяем горизонты и пласты
    const originalHorizons = originalWell.horizonsInfo?.map((h: any) => h.id).sort().join(',') || '';
    const currentHorizons = currentWell.horizonsInfo?.map((h: any) => h.id).sort().join(',') || '';
    if (originalHorizons !== currentHorizons) return true;

    const originalLayers = originalWell.layersInfo?.map((l: any) => l.id).sort().join(',') || '';
    const currentLayers = currentWell.layersInfo?.map((l: any) => l.id).sort().join(',') || '';
    if (originalLayers !== currentLayers) return true;

    return false;
  };

  // Обработчик для массового сохранения данных скважин
  // Обработчик для массового сохранения данных скважин
const handleSave = async () => {
  if (!projectUuid) {
    console.error('❌ projectUuid не определен');
    return;
  }

  if (editableRows.length === 0) {
    console.warn('⚠️ Нет данных для сохранения');
    return;
  }

  try {
    // Разделяем скважины на новые и существующие
    const newWells = editableRows.filter(row => !row.uuid);
    const existingWells = editableRows.filter(row => row.uuid);

    // 1. Создаем новые скважины через bulk
    if (newWells.length > 0) {
      const wellsToCreate = newWells.map((row) => {
        const areaUuid = row.areaName?.id || '';
        const categoryUuid = row.categoryName?.id || '';
        const researchObjectiveUuid = row.researchObjective?.id || '';
        const studiedPhaseUuid = row.studiedPhase?.id || '';

        const horizonIds = Array.isArray(row.horizonsInfo)
          ? row.horizonsInfo.map((h: any) => h?.id).filter(Boolean)
          : [];

        const layerIds = Array.isArray(row.layersInfo)
          ? row.layersInfo.map((l: any) => l?.id).filter(Boolean)
          : [];

        return {
          number: parseInt(row.wellNumber) || 0,
          workshop: parseInt(row.workshop) || 0,
          team: parseInt(row.brigade) || 0,
          liquid_rate: parseFloat(row.liquidRate) || 0,
          water_cut: parseFloat(row.waterCut) || 0,
          note: row.note || "",
          is_sample_taken: row.isSampleTaken || false,
          sample_note: row.sampleNote || "",
          is_oil_sample_received: row.isOilSampleReceived || false,
          oil_volume: parseFloat(row.oilVolume) || 0,
          is_oil_split: row.isOilSplit || false,
          is_water_sample_received: row.isWaterSampleReceived || false,
          water_volume: parseFloat(row.waterVolume) || 0,
          is_water_split: row.isWaterSplit || false,
          water_density: parseFloat(row.waterDensity) || 0,
          water_ec: parseFloat(row.waterEc) || 0,
          is_gh: row.isGh || false,
          is_icp: row.isIcp || false,
          is_hav: row.isHav || false,
          is_kapel: row.isKapel || false,
          result: "",
          is_editable: true,
          x_coords: parseFloat(row.xCoord) || 0,
          y_coords: parseFloat(row.yCoord) || 0,
          oil_rate: parseFloat(row.qOil) || 0,
          studied_phase: studiedPhaseUuid,
          research_objective: researchObjectiveUuid,
          category: categoryUuid,
          area: areaUuid,
          horizons: horizonIds,
          layers: layerIds,
          project: projectUuid
        };
      });

      console.log(`📦 Создание ${wellsToCreate.length} новых скважин через bulk`);
      //@ts-ignore
      const result = await postBulkWell({ wells: wellsToCreate }).unwrap();
      console.log('✅ Bulk создание успешно:', result);
      
      // Обновляем UUID для новых скважин
      if (result && Array.isArray(result)) {
        let createdIndex = 0;
        setEditableRows(prevRows =>
          prevRows.map(row => {
            if (!row.uuid) {
              const newWell = result[createdIndex];
              createdIndex++;
              if (newWell) {
                return { ...row, uuid: newWell.uuid || newWell.id };
              }
            }
            return row;
          })
        );
      }
    }

    // 2. Обновляем существующие скважины параллельно через patch
    if (existingWells.length > 0) {
      console.log(`🔄 Обновление ${existingWells.length} существующих скважин (параллельно)`);
      
      // Создаем массив промисов для patch-запросов
      const patchPromises = existingWells.map(async (row) => {
        try {
          const horizonIds = Array.isArray(row.horizonsInfo)
            ? row.horizonsInfo.map((h: any) => h?.id).filter(Boolean)
            : [];

          const layerIds = Array.isArray(row.layersInfo)
            ? row.layersInfo.map((l: any) => l?.id).filter(Boolean)
            : [];

          // Подготавливаем данные для patch
          const patchData = {
            uuid: row.uuid,
            number: parseInt(row.wellNumber) || 0,
            workshop: parseInt(row.workshop) || 0,
            team: parseInt(row.brigade) || 0,
            liquid_rate: parseFloat(row.liquidRate) || 0,
            water_cut: parseFloat(row.waterCut) || 0,
            note: row.note || "",
            is_sample_taken: row.isSampleTaken || false,
            sample_note: row.sampleNote || "",
            is_oil_sample_received: row.isOilSampleReceived || false,
            oil_volume: parseFloat(row.oilVolume) || 0,
            is_oil_split: row.isOilSplit || false,
            is_water_sample_received: row.isWaterSampleReceived || false,
            water_volume: parseFloat(row.waterVolume) || 0,
            is_water_split: row.isWaterSplit || false,
            water_density: parseFloat(row.waterDensity) || 0,
            water_ec: parseFloat(row.waterEc) || 0,
            is_gh: row.isGh || false,
            is_icp: row.isIcp || false,
            is_hav: row.isHav || false,
            is_kapel: row.isKapel || false,
            x_coords: parseFloat(row.xCoord) || 0,
            y_coords: parseFloat(row.yCoord) || 0,
            oil_rate: parseFloat(row.qOil) || 0,
            studied_phase: row.studiedPhase?.id || '',
            research_objective: row.researchObjective?.id || '',
            category: row.categoryName?.id || '',
            area: row.areaName?.id || '',
            horizons: horizonIds,
            layers: layerIds
          };

          // Отправляем patch запрос
          await patchWell(patchData).unwrap();
          console.log(`✅ Скважина ${row.wellNumber} успешно обновлена`);

        } catch (error) {
          console.error(`❌ Ошибка при обновлении скважины ${row.wellNumber}:`, error);
          throw error;
        }
      });

      // Ждем завершения всех patch запросов
      await Promise.all(patchPromises);
      console.log('✅ Все существующие скважины обновлены');
    }

    // 3. Перезагружаем данные
    try {
      isInitializedRef.current = false;
      await triggerGetWell({ project: projectUuid, limit: 9999 }).unwrap();
    } catch (error) {
      console.error('❌ Ошибка при перезагрузке данных:', error);
    }

  } catch (err) {
    console.error('❌ Общая ошибка при сохранении данных скважин:', err);
  }
};

  // Функция для перехода на следующий этап с обновлением current_stage
  const handleOpenNextStage = async () => {
    try {
      await handleSave();

      if (projectUuid) {
        await patchProject({
          uuid: projectUuid,
          current_stage: 3,
        }).unwrap();
      }

      navigate(`/project/${projectUuid}/monitoring/3`);

    } catch (error) {
      console.error('❌ Ошибка при переходе на следующий этап:', error);
    }
  };

  // Обработчик открытия модального окна загрузки данных
  const handleLoadDataClick = () => {
    if (projectUuid) {
      setIsLoadDataModalOpen(true);
    } else {
      console.error('❌ projectUuid не доступен для загрузки данных');
    }
  };

  // Обработчик успешной загрузки данных
  const handleLoadDataSuccess = async () => {
    try {
      setIsLoadDataModalOpen(false);
      isInitializedRef.current = false;
      initializationInProgressRef.current = false;
      await triggerGetWell({ project: projectUuid, limit: 9999 }).unwrap();
    } catch (error) {
      console.error('❌ Ошибка при обновлении данных после загрузки:', error);
    }
  };

  // Добавляем обработчик открытия модального окна
  const handleOpenTableModal = () => {
    setIsTableModalOpen(true);
  };

  // Добавляем обработчик закрытия модального окна
  const handleCloseTableModal = () => {
    setIsTableModalOpen(false);
  };

  // Функция для добавления новой строки
  const handleAddWell = () => {
    const newRowId = `new-${Date.now()}`;
    const newRow = {
      id: newRowId,
      uuid: '',
      wellNumber: '',
      workshop: '',
      brigade: '',
      areaName: null,
      areaNameText: '',
      horizonsInfo: [],
      horizonsInfoText: '',
      layersInfo: [],
      layersInfoText: '',
      qLiquid: '',
      qOil: '',
      liquidRate: '',
      waterCut: '',
      categoryName: null,
      categoryNameText: '',
      xCoord: '',
      yCoord: '',
      studiedPhase: null,
      studiedPhaseText: '',
      researchObjective: null,
      researchObjectiveText: '',
      note: '',
      isSampleTaken: false,
      sampleNote: '',
      isOilSampleReceived: false,
      oilVolume: '',
      isOilSplit: false,
      isWaterSampleReceived: false,
      waterVolume: '',
      isWaterSplit: false,
      waterDensity: '',
      waterEc: '',
      isGh: false,
      isIcp: false,
      isHav: false,
      isKapel: false,
      availableHorizons: [],
      availableLayers: [],
      isLoadingHorizons: false,
      isLoadingLayers: false,
      isDeleting: false
    };

    setEditableRows(prevRows => [...prevRows, newRow]);
    setJustAddedWell(true);
    autoSave.resetTimer();
  };

  const handleHorizonChange = (rowId: string, value: any) => {
    const selectedHorizons = Array.isArray(value) ? value : [];
    const horizonsText = selectedHorizons.map(h => h.label).join(', ');

    setEditableRows(prevRows => {
      const newRows = prevRows.map(row => {
        if (row.id === rowId) {
          return {
            ...row,
            horizonsInfo: selectedHorizons,
            horizonsInfoText: horizonsText,
            isLoadingLayers: selectedHorizons.length > 0
          };
        }
        return row;
      });
      return newRows;
    });

    autoSave.resetTimer();

    if (selectedHorizons.length > 0) {
      const loadLayers = async () => {
        try {
          const horizonLayerPromises = selectedHorizons.map(async (horizon) => {
            if (horizon?.id) {
              return await loadLayersByHorizon(horizon.id);
            }
            return [];
          });

          const layersResults = await Promise.allSettled(horizonLayerPromises);

          const allLayers: any[] = [];
          layersResults.forEach(result => {
            if (result.status === 'fulfilled') {
              allLayers.push(...result.value);
            }
          });

          const uniqueLayers = allLayers.filter((layer, index, self) =>
            index === self.findIndex(l => l.id === layer.id)
          );

          setEditableRows(prev => {
            const updatedRows = prev.map(r => {
              if (r.id === rowId) {
                return { ...r, availableLayers: uniqueLayers, isLoadingLayers: false };
              }
              return r;
            });
            return updatedRows;
          });
        } catch (error) {
          console.error('❌ Ошибка загрузки пластов:', error);
          setEditableRows(prev => {
            const updatedRows = prev.map(r => {
              if (r.id === rowId) {
                return { ...r, availableLayers: [], isLoadingLayers: false };
              }
              return r;
            });
            return updatedRows;
          });
        }
      };

      loadLayers();
    } else {
      setEditableRows(prev => {
        const updatedRows = prev.map(r => {
          if (r.id === rowId) {
            return {
              ...r,
              availableLayers: [],
              layersInfo: [],
              layersInfoText: '',
              isLoadingLayers: false
            };
          }
          return r;
        });
        return updatedRows;
      });
    }
  };

  const handleAreaChange = (rowId: string, value: any) => {
    setEditableRows(prevRows => {
      const newRows = prevRows.map(row => {
        if (row.id === rowId) {
          const updatedRow = {
            ...row,
            areaName: value,
            areaNameText: value?.label || '',
            isLoadingHorizons: !!value
          };

          if (row.areaName?.id !== value?.id) {
            updatedRow.horizonsInfo = [];
            updatedRow.horizonsInfoText = '';
            updatedRow.availableHorizons = [];
            updatedRow.layersInfo = [];
            updatedRow.layersInfoText = '';
            updatedRow.availableLayers = [];
          }

          return updatedRow;
        }
        return row;
      });
      return newRows;
    });

    autoSave.resetTimer();

    if (value?.id) {
      const loadHorizons = async () => {
        try {
          const horizons = await loadHorizonsByArea(value.id);

          setEditableRows(prev => {
            const updatedRows = prev.map(r => {
              if (r.id === rowId) {
                return { ...r, availableHorizons: horizons, isLoadingHorizons: false };
              }
              return r;
            });
            return updatedRows;
          });
        } catch (error) {
          console.error('❌ Ошибка загрузки горизонтов:', error);
          setEditableRows(prev => {
            const updatedRows = prev.map(r => {
              if (r.id === rowId) {
                return { ...r, availableHorizons: [], isLoadingHorizons: false };
              }
              return r;
            });
            return updatedRows;
          });
        }
      };

      loadHorizons();
    } else {
      setEditableRows(prev => {
        const updatedRows = prev.map(r => {
          if (r.id === rowId) {
            return {
              ...r,
              availableHorizons: [],
              horizonsInfo: [],
              horizonsInfoText: '',
              availableLayers: [],
              layersInfo: [],
              layersInfoText: '',
              isLoadingHorizons: false
            };
          }
          return r;
        });
        return updatedRows;
      });
    }
  };

  const handleLayersChange = (rowId: string, value: any) => {
    const selectedLayers = Array.isArray(value) ? value : [];
    const layersText = selectedLayers.map(l => l.label).join(', ');

    setEditableRows(prevRows => {
      const newRows = prevRows.map(row => {
        if (row.id === rowId) {
          return { ...row, layersInfo: selectedLayers, layersInfoText: layersText };
        }
        return row;
      });
      return newRows;
    });

    autoSave.resetTimer();
  };

  // Кастомный компонент кнопки удаления
  const DeleteButton = ({ row, onDelete, isDeleting }: any) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          iconLeft={IconTrash}
          view="clear"
          size="s"
          onClick={() => onDelete(row?.id)}
          disabled={isDeleting}
          loading={isDeleting}
          title={isDeleting ? "Удаление..." : `Удалить скважину №${row?.wellNumber}`}
          style={{
            color: isDeleting ? '#8c8c8c' : '#ff4d4f'
          }}
        />
      </div>
    );
  };

  // Колонки таблицы с простым инлайн рендерингом
  const columnsMonitoring = [
    {
      title: '№',
      accessor: 'rowNumber',
      sortable: 'true',
      width: 60,
      renderCell: (row: any) => {
        return (
          <div>
            {row.rowIndex}
          </div>
        );
      }
    },
    {
      title: '№ скважины',
      accessor: 'wellNumber',
      sortable: 'true',
      width: 150,
      renderCell: (row: any) => (
        <TextField
          value={row.wellNumber}
          size='s'
          onChange={handleTextFieldChange(row.id, 'wellNumber')}
          placeholder="Введите номер"
        />
      )
    },
    {
      title: 'Цех',
      accessor: 'workshop',
      sortable: 'true',
      renderCell: (row: any) => (
        <TextField
          value={row.workshop}
          size='s'
          onChange={handleTextFieldChange(row.id, 'workshop')}
          placeholder="Введите цех"
        />
      )
    },
    {
      title: 'Бригада',
      accessor: 'brigade',
      sortable: 'true',
      renderCell: (row: any) => (
        <TextField
          value={row.brigade}
          size='s'
          onChange={handleTextFieldChange(row.id, 'brigade')}
          placeholder="Введите бригаду"
        />
      )
    },
    {
      title: 'Участок',
      accessor: 'areaName',
      sortable: 'true',
      width: 170,
      renderCell: (row: any) => (
        <Select
          items={areaItems}
          value={row.areaName}
          onChange={(value) => handleAreaChange(row.id, value)}
          size='s'
          placeholder="Выберите участок"
          disabled={areaItems.length === 0}
        />
      )
    },
    {
      title: 'Шифр горизонта',
      accessor: 'horizonsInfo',
      sortable: 'true',
      width: 200,
      renderCell: (row: any) => (
        <Combobox
          items={row.availableHorizons}
          value={row.horizonsInfo}
          onChange={(value) => handleHorizonChange(row.id, value)}
          size='s'
          multiple={true}
          placeholder={row.isLoadingHorizons ? "Загрузка..." : (row.areaName ? "Выберите горизонт" : "Сначала выберите участок")}
          disabled={!row.areaName || row.isLoadingHorizons || row.availableHorizons.length === 0}
        />
      )
    },
    {
      title: 'Пласты',
      accessor: 'layersInfo',
      sortable: 'true',
      width: 200,
      renderCell: (row: any) => (
        <Combobox
          items={row.availableLayers}
          value={row.layersInfo}
          onChange={(value) => handleLayersChange(row.id, value)}
          size='s'
          multiple={true}
          placeholder={row.isLoadingLayers ? "Загрузка..." : (row.horizonsInfo && row.horizonsInfo.length > 0 ? "Выберите пласт" : "Сначала выберите горизонт")}
          disabled={!row.horizonsInfo || row.horizonsInfo.length === 0 || row.isLoadingLayers || row.availableLayers.length === 0}
        />
      )
    },
    {
      title: 'Q жидкости (т/сут)',
      accessor: 'qLiquid',
      width: 200,
      sortable: 'true',
      renderCell: (row: any) => (
        <TextField
          value={row.qLiquid}
          size='s'
          onChange={handleTextFieldChange(row.id, 'qLiquid')}
          placeholder="Введите Q жидкости"
        />
      )
    },
    {
      title: 'Q нефти (т/сут)',
      accessor: 'qOil',
      sortable: 'true',
      width: 200,
      renderCell: (row: any) => (
        <TextField
          value={row.qOil}
          size='s'
          onChange={handleTextFieldChange(row.id, 'qOil')}
          placeholder="Введите Q нефти"
        />
      )
    },
    {
      title: 'Дебит скважины по жидкости (м³/сут)',
      accessor: 'liquidRate',
      sortable: 'true',
      width: 200,
      renderCell: (row: any) => (
        <TextField
          value={row.liquidRate}
          size='s'
          onChange={handleTextFieldChange(row.id, 'liquidRate')}
          placeholder="Введите дебит"
        />
      )
    },
    {
      title: 'Обводнённость (%)',
      accessor: 'waterCut',
      width: 200,
      sortable: 'true',
      renderCell: (row: any) => (
        <TextField
          value={row.waterCut}
          size='s'
          onChange={handleTextFieldChange(row.id, 'waterCut')}
          placeholder="Введите обводненность"
        />
      )
    },
    {
      title: 'Категория',
      accessor: 'categoryName',
      sortable: 'true',
      width: 200,
      renderCell: (row: any) => (
        <Select
          items={categoryItems}
          value={row.categoryName}
          onChange={handleSelectChange(row.id, 'categoryName')}
          size='s'
          placeholder="Выберите категорию"
          disabled={categoryItems.length === 0}
        />
      )
    },
    {
      title: 'Координаты X',
      accessor: 'xCoord',
      sortable: 'true',
      width: 150,
      renderCell: (row: any) => (
        <TextField
          value={row.xCoord}
          size='s'
          onChange={handleTextFieldChange(row.id, 'xCoord')}
          placeholder="Введите X"
        />
      )
    },
    {
      title: 'Координаты Y',
      accessor: 'yCoord',
      sortable: 'true',
      width: 150,
      renderCell: (row: any) => (
        <TextField
          value={row.yCoord}
          size='s'
          onChange={handleTextFieldChange(row.id, 'yCoord')}
          placeholder="Введите Y"
        />
      )
    },
    {
      title: 'Исследуемая фаза',
      accessor: 'studiedPhase',
      sortable: 'true',
      width: 150,
      renderCell: (row: any) => (
        <Select
          items={studiedPhaseItems}
          value={row.studiedPhase}
          onChange={handleSelectChange(row.id, 'studiedPhase')}
          size='s'
          placeholder="Выберите фазу"
          disabled={studiedPhaseItems.length === 0}
        />
      )
    },
    {
      title: 'Цель исследования',
      accessor: 'researchObjective',
      sortable: 'true',
      width: 200,
      renderCell: (row: any) => (
        <Select
          items={researchObjectiveItems}
          value={row.researchObjective}
          onChange={handleSelectChange(row.id, 'researchObjective')}
          size='s'
          placeholder="Выберите цель"
          disabled={researchObjectiveItems.length === 0}
        />
      )
    },
    {
      title: '',
      accessor: 'action',
      width: 100,
      renderCell: (row: any) => (
        <DeleteButton
          row={row}
          onDelete={handleDeleteWithLoading}
          isDeleting={isDeleting && row.isDeleting}
        />
      )
    },
  ];

  const isLoading = isLoadingStage2 || isLoadingStage1 || isLoadingWells || isLoadingCategories ||
    isLoadingHorizons || isLoadingLayers || isLoadingResearchObjectives || isLoadingStudiedPhases;

  return (
    <>
      <div className={classes.header}>
        <Text size='2xl' weight='bold'>Мониторинг проекта - Этап 2</Text>
        <Text size='s' view='secondary'>Работа со скважинами</Text>
        {isLoading && <Text size='s' view='secondary'>Загрузка данных...</Text>}
        {areaItems.length === 0 && !isLoading && (
          <Text size='s' view="warning">Участки не выбраны на этапе 1</Text>
        )}
      </div>

      <div className={classes.cardMonitoring}>
        <Card className={classes.card}>
          <div className={classes.headerCard}>
            <Text size='xl' weight='bold'>Скважины проекта</Text>
            <div className={classes.headerButtons}>
              <Button
                size='s'
                onClick={handleOpenTableModal}
                disabled={isDeleting || editableRows.length === 0}
                iconLeft={IconExpand}
                view='ghost'
              />
              <Button
                size='s'
                label='Добавить скважину'
                style={{ backgroundColor: 'green' }}
                onClick={handleAddWell}
                disabled={isDeleting}
              />
              <Button
                size='s'
                label='Загрузить данные'
                onClick={handleLoadDataClick}
                disabled={isDeleting}
              />
            </div>
          </div>

          <div className={classes.centerCard}>
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Text>Загрузка данных скважин...</Text>
              </div>
            ) : editableRows.length > 0 ? (
              <>
                <div className={classes.tableContainer}>
                  <Table
                    className={classes.monitoringTable}
                    rows={paginatedRows}
                    stickyColumns={1 && 2}
                    stickyHeader
                    columns={columnsMonitoring}
                    borderBetweenRows
                    verticalAlign="top"
                    size='s'
                    getRowKey={(row: any) => row.id}
                  />
                </div>

                {totalPages > 1 && (
                  <div className={classes.paginationSection} style={{
                    marginTop: '20px',
                    marginBottom: '10px',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Pagination
                      items={totalPages}
                      value={currentPage}
                      onChange={handlePageChange}
                      showFirstPage
                      showLastPage
                      visibleCount={7}
                      size='s'
                    />
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', alignItems: 'center', flexDirection: 'column', gap: '16px' }}>
                <Text view="secondary">Нет данных о скважинах</Text>
                <Button
                  label="Добавить первую скважину"
                  onClick={handleAddWell}
                  disabled={isDeleting}
                />
              </div>
            )}
          </div>

          <div className={classes.footer}>
            <div>
              <Text view='secondary' size='s'>Доступно сохранение до 1000 скважин</Text>
            </div>
            <div className={classes.footerButton}>
            <Button
              label='Назад'
              view='ghost'
              onClick={() => navigate(`/project/${projectUuid}/monitoring`)}
              disabled={isDeleting}
            />
            <Button
              label={isSavingBulk || isPatching ? "Сохранение..." : "Сохранить"}
              onClick={handleSave}
              loading={isSavingBulk || isPatching}
              disabled={isSavingBulk || isPatching || isDeleting || !projectUuid || editableRows.length === 0}
              title={editableRows.length === 0 ? "Нет данных для сохранения" : "Сохранить данные и обновить таблицу"}
            />
            <Button
              label={isGeneratingReport ? "Формирование..." : "Сформировать мини-отчет"}
              style={{ backgroundColor: 'purple' }}
              onClick={handleGenerateMiniReport}
              loading={isGeneratingReport}
              disabled={isGeneratingReport || isDeleting || !projectUuid}
            />
            <Button
              label='Сформировать программу отбора'
              style={{ backgroundColor: 'green' }}
              onClick={handleOpenNextStage}
              loading={isUpdatingStage}
              disabled={isDeleting || isUpdatingStage}
            />
            </div>
          </div>
        </Card>
      </div>

      {/* Модальное окно с таблицей скважин */}
      <Modal
        isOpen={isTableModalOpen}
        onClose={handleCloseTableModal}
        hasOverlay
        className={classes.modalFullScreen}
      >
        <div className={classes.modalTable}>
          <Table
            rows={paginatedRows}
            stickyColumns={1 && 2}
            stickyHeader
            columns={columnsMonitoring}
            borderBetweenRows
            verticalAlign="top"
            size='s'
            getRowKey={(row: any) => row.id}
          />

          {totalPages > 1 && (
            <div className={classes.paginationSection} style={{
              marginTop: '20px',
              marginBottom: '10px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Pagination
                items={totalPages}
                value={currentPage}
                onChange={handlePageChange}
                showFirstPage
                showLastPage
                visibleCount={7}
                size='s'
              />
            </div>
          )}
        </div>
        <div className={classes.modalButtons}>
          <Button
            label='Добавить скважину'
            style={{ backgroundColor: 'green' }}
            onClick={handleAddWell}
            disabled={isDeleting}
          />
          <Button
            label='Закрыть'
            onClick={handleCloseTableModal}
          />
        </div>
      </Modal>

      <LoadDataModal
        isOpen={isLoadDataModalOpen}
        onClose={() => setIsLoadDataModalOpen(false)}
        projectUuid={projectUuid}
        onSuccess={handleLoadDataSuccess}
      />
    </>
  );
};

// Тип для строки таблицы настройки группировки
type GroupingRow = {
  id: string;
  rowNumber: number;
  wellTypes: string[];
  waterCutFrom: string;
  waterCutTo: string;
  volume: string;
};

// Тип для данных группировки в формате API
type GroupingSetting = {
  start: number;
  end: number;
  types: string[];
  volume: number;
};

interface Stage3Props {
  navigate: (path: string) => void;
  projectUuid: string;
  onOpenModal?: (stage: string) => void;
}

const Stage3 = ({ navigate, projectUuid, onOpenModal }: Stage3Props) => {
  // Состояние для модального окна
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [groupingRows, setGroupingRows] = useState<GroupingRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const getPaginatedData = (data: any[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const addIndexToData = (data: any[], page: number) => {
    return data.map((item, idx) => ({
      ...item,
      rowNumber: (page - 1) * itemsPerPage + idx + 1
    }));
  };


  // Получение категорий скважин (типов)
  const [getWellCategories, { data: wellCategories, isLoading: isLoadingCategories }] =
    useLazyGetWellCategorySelectorQuery();

  // Получение данных для этапа 3
  const [getStage3Data, { data: stage3Data, isLoading: isLoadingStage3 }] =
    useLazyGetProjectCurrentStageInfoQuery();

  // Мутация для обновления проекта
  const [patchProject, { isLoading: isUpdatingStage }] = usePatchProjectMutation();

  useEffect(() => {
    if (projectUuid) {
      getStage3Data({
        uuid: projectUuid,
        current_stage: 3
      });
    }
  }, [projectUuid, getStage3Data]);

  // Загрузка категорий скважин при открытии модального окна
  useEffect(() => {
    if (isModalOpen) {
      getWellCategories({});
    }
  }, [isModalOpen, getWellCategories]);

  // Загрузка данных в модальное окно из groups при его открытии
  useEffect(() => {
    if (isModalOpen && stage3Data?.groups && stage3Data.groups.length > 0) {
      // Преобразуем группы в строки для модального окна
      const rows: GroupingRow[] = stage3Data.groups.map((group: any, index: any) => ({
        id: `row-${Date.now()}-${index}`,
        rowNumber: index + 1,
        wellTypes: group.config.types || [], // Если types: null, будет пустой массив
        waterCutFrom: group.config.start?.toString() || '0',
        waterCutTo: group.config.end?.toString() || '100',
        volume: group.config.volume?.toString() || '0',
      }));
      setGroupingRows(rows);
    } else if (isModalOpen && groupingRows.length === 0) {
      // Если нет groups, добавляем пустую строку с дефолтными значениями
      addNewRow();
    }
  }, [isModalOpen, stage3Data]);

  // Преобразование категорий скважин в формат для комбобокса
  const getWellTypeItems = () => {
    if (!wellCategories) return [];

    const categories = Array.isArray(wellCategories)
      ? wellCategories
      : wellCategories?.results || [];

    return categories;
  };

  const handleOpenNextStage = async () => {
    try {
      if (projectUuid) {
        await patchProject({
          uuid: projectUuid,
          current_stage: 4,
        }).unwrap();
      }
      navigate(`/project/${projectUuid}/monitoring/4`);
    } catch (error) {
      console.error('❌ Ошибка при переходе на следующий этап:', error);
    }
  };


  // Компонент для отображения типов скважин в заголовке
  const TypesDisplay = ({ types }: { types: string[] | null }) => {
    if (!types || types.length === 0) return null; // Ничего не отображаем, если типов нет
    if (types.length === 1) return <span>{types[0]}</span>;
    return <span>{types[0]} +{types.length - 1}</span>;
  };

  // Компонент для заголовка с тултипом
  // Компонент для заголовка с тултипом
  const HeaderWithTooltip = ({ group }: { group: any }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const headerRef = useRef<HTMLDivElement>(null);

    const updatePosition = () => {
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + window.scrollY + 5,
          left: rect.left + window.scrollX,
        });
      }
    };

    const handleMouseEnter = () => {
      updatePosition();
      setShowTooltip(true);
    };

    const handleMouseLeave = () => {
      setShowTooltip(false);
    };

    // Определяем типы для отображения в тултипе
    const typesToDisplay = group?.config?.types?.length > 0
      ? group.config.types.join(', ')
      : 'Все типы скважин';

    return (
      <div
        ref={headerRef}
        style={{
          position: 'relative',
          textAlign: 'center',
          cursor: 'help'
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div style={{ fontWeight: 'bold' }}>
          <TypesDisplay types={group.config.types} />
        </div>
        <div>{group.config.start}% - {group.config.end}%</div>
        <div>{group.config.volume} л</div>

        {showTooltip && createPortal(
          <div style={{
            position: 'absolute',
            top: position.top,
            left: position.left,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '12px',
            zIndex: 10000,
            minWidth: '250px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            textAlign: 'left',
          }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <div style={{ marginBottom: '8px' }}>
              <strong>Типы скважин:</strong>
              <div style={{ marginTop: '4px', color: '#333' }}>
                {typesToDisplay}
              </div>
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>Обводненность:</strong> {group?.config.start}% - {group.config.end}%
            </div>
            <div>
              <strong>Объем:</strong> {group?.config.volume} л
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  // Функция для создания заголовка колонки
  const getColumnTitle = (group: any) => {
    return <HeaderWithTooltip group={group} />;
  };

  // Функция для создания строк таблицы
  const getTableData = () => {
    if (!stage3Data?.groups) {
      return [];
    }

    // Находим максимальное количество скважин в группах
    const maxWellsCount = Math.max(
      ...stage3Data.groups.map((group: any) => group.wells?.length || 0)
    );

    const rows = [];

    // Создаем строки для каждой скважины
    for (let i = 0; i < maxWellsCount; i++) {
      const row: any = {
        id: `row-${i}`,
      };

      // Для каждой группы добавляем номер скважины на позиции i
      stage3Data.groups.forEach((group: any, groupIndex: any) => {
        const well = group.wells?.[i];
        row[`group${groupIndex}`] = well ? `Скважина ${well.number}` : '';
      });

      rows.push(row);
    }

    // Если нет скважин, показываем одну строку с "Нет данных"
    if (maxWellsCount === 0) {
      const emptyRow: any = {
        id: 'empty',
      };
      stage3Data.groups.forEach((_: any, groupIndex: any) => {
        emptyRow[`group${groupIndex}`] = 'Нет данных';
      });
      return [emptyRow];
    }

    return rows;
  };

  // Создаем колонки на основе групп
  const getColumns = () => {
    if (!stage3Data?.groups) {
      return [];
    }

    const columns: any = [];

    // Добавляем колонку для каждой группы
    stage3Data.groups.forEach((group: any, index: any) => {
      columns.push({
        title: getColumnTitle(group),
        align: 'center',
        accessor: `group${index}`,
        width: `${100 / stage3Data.groups.length}%`,
        renderCell: (row: any) => (
          <Text>{row[`group${index}`]}</Text>
        )
      });
    });

    return columns;
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setGroupingRows([]);
  };

  const addNewRow = () => {
    const newRow: GroupingRow = {
      id: `row-${Date.now()}-${groupingRows.length}`,
      rowNumber: groupingRows.length + 1,
      wellTypes: [], // Пустой массив будет означать "Все типы"
      waterCutFrom: '0',
      waterCutTo: '100',
      volume: '0',
    };
    setGroupingRows([...groupingRows, newRow]);
  };

  const deleteRow = (id: string) => {
    const filteredRows = groupingRows.filter(row => row.id !== id);
    const updatedRows = filteredRows.map((row, index) => ({
      ...row,
      rowNumber: index + 1,
    }));
    setGroupingRows(updatedRows);
  };

  const updateRow = (id: string, field: keyof GroupingRow, value: any) => {
    setGroupingRows(prevRows =>
      prevRows.map(row =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const prepareGroupingData = (): GroupingSetting[] => {
    // Фильтруем только заполненные строки (проверяем только обводненность и объем)
    const validRows = groupingRows.filter(row => {
      const hasWaterCutFrom = row.waterCutFrom !== undefined && row.waterCutFrom !== null && row.waterCutFrom !== '';
      const hasWaterCutTo = row.waterCutTo !== undefined && row.waterCutTo !== null && row.waterCutTo !== '';
      const hasVolume = row.volume !== undefined && row.volume !== null && row.volume !== '';

      return hasWaterCutFrom && hasWaterCutTo && hasVolume;
    });

    return validRows.map((row: any) => ({
      start: parseFloat(row.waterCutFrom),
      end: parseFloat(row.waterCutTo),
      types: row.wellTypes.length > 0 ? row.wellTypes : null, // Если массив пустой, отправляем null
      volume: parseFloat(row.volume),
    }));
  };

  const handleSaveGrouping = async () => {
    try {
      const groupingData: any = prepareGroupingData();

      if (groupingData.length === 0) {
        console.warn('Нет заполненных строк для сохранения');
        return;
      }

      console.log('💾 Отправляемые данные (массив объектов):', groupingData);

      if (projectUuid) {
        await patchProject({
          uuid: projectUuid,
          grouping_settings: groupingData,
        }).unwrap();

        // Обновляем данные после сохранения
        await getStage3Data({
          uuid: projectUuid,
          current_stage: 3
        });

        handleCloseModal();
      }
    } catch (error) {
      console.error('❌ Ошибка при сохранении настроек группировки:', error);
    }
  };

  const groupingColumns = [
    {
      title: '№',
      accessor: 'rowNumber',
      width: '5%',
      renderCell: (row: GroupingRow) => (
        <Text>{row.rowNumber}</Text>
      )
    },
    {
      title: 'Типы скважин',
      accessor: 'wellTypes',
      width: '25%',
      renderCell: (row: GroupingRow) => {
        const items = getWellTypeItems();

        // Находим выбранные объекты по названиям
        const selectedItems = items.filter((item: any) => row.wellTypes.includes(item.name));

        return (
          <Combobox
            placeholder="Выберите типы"
            items={items}
            value={selectedItems}
            onChange={(value) => {
              const selectedNames = value ? value.map(item => item.name) : [];
              updateRow(row.id, 'wellTypes', selectedNames);
            }}
            getItemLabel={(item: any) => item.name}
            getItemKey={(item: any) => item.uuid}
            multiple
            size="s"
            style={{ width: '100%' }}
            disabled={isLoadingCategories}
          />
        );
      }
    },
    {
      title: 'Обводненность',
      accessor: 'waterCut',
      width: '30%',
      renderCell: (row: GroupingRow) => (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <TextField
            placeholder="От"
            value={row.waterCutFrom}
            onChange={(value) => updateRow(row.id, 'waterCutFrom', value)}
            type="number"
            size="s"
            style={{ width: '80px' }}
            min={0}
            max={100}
          />
          <Text>—</Text>
          <TextField
            placeholder="До"
            value={row.waterCutTo}
            onChange={(value) => updateRow(row.id, 'waterCutTo', value)}
            type="number"
            size="s"
            style={{ width: '80px' }}
            min={0}
            max={100}
          />
        </div>
      )
    },
    {
      title: 'Объем',
      accessor: 'volume',
      width: '25%',
      renderCell: (row: GroupingRow) => (
        <TextField
          placeholder="Введите объем"
          value={row.volume}
          onChange={(value) => updateRow(row.id, 'volume', value)}
          type="number"
          size="s"
          style={{ width: '100%' }}
          min={0}
        />
      )
    },
    {
      title: '',
      accessor: 'actions',
      width: '5%',
      renderCell: (row: GroupingRow) => (
        <Button
          iconLeft={IconTrash}
          size="s"
          view="ghost"
          onClick={() => deleteRow(row.id)}
          disabled={groupingRows.length <= 1}
        />
      )
    },
  ];

  const tableData = getTableData();
  const columns = getColumns();

  const paginatedTableData = getPaginatedData(tableData, currentPage);
  const totalPages = Math.ceil(tableData.length / itemsPerPage);

  // Обработчик смены страницы
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Сбрасываем страницу при изменении данных
  useEffect(() => {
    setCurrentPage(1);
  }, [tableData.length]);

  return (
    <>
      <div className={classes.header}>
        <Text size='2xl' weight='bold'>Мониторинг проекта - Этап 3</Text>
        <Text size='s' view='secondary'>Формирование программы отбора проб и этикеток</Text>
        {isLoadingStage3 && <Text size='s' view='secondary'>Загрузка данных...</Text>}
      </div>

      <div className={classes.cardMonitoring}>
        <Card className={classes.card}>
          <div className={classes.headerCard}>
            <Text size='xl' weight='bold'>Группировка скважин по обводненности</Text>
            <Button
              label="Настроить группировку"
              onClick={handleOpenModal}
              size="s"
              view="secondary"
            />
          </div>

          <div className={classes.centerCard}>
            {isLoadingStage3 ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px'
              }}>
                <Loader />
              </div>
            ) : tableData.length > 0 ? (
              <>
                <div className={classes.tableContainer}>
                  <Table
                    className={classes.monitoringTableContainer}
                    rows={paginatedTableData} // Используем пагинированные данные
                    size='s'
                    stickyHeader
                    columns={columns}
                    borderBetweenRows
                  />
                </div>

                {totalPages > 1 && (
                  <div className={classes.paginationSection} style={{
                    marginTop: '20px',
                    marginBottom: '10px',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Pagination
                      items={totalPages}
                      value={currentPage}
                      onChange={handlePageChange}
                      showFirstPage
                      showLastPage
                      visibleCount={7}
                      size='s'
                    />
                  </div>
                )}
              </>
            ) : (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px'
              }}>
                <Text view="secondary">Нет данных для отображения</Text>
              </div>
            )}
          </div>

          <div className={classes.footerCard}>
            <Button
              label='Назад'
              view='ghost'
              onClick={() => navigate(`/project/${projectUuid}/monitoring/2`)}
            />
            <Button
              label='Сформировать этикетку для тары'
              onClick={() => onOpenModal?.('stage3')}
              disabled={!stage3Data?.groups || stage3Data.groups.length === 0}
            />
            <Button
              label='Выполнить сверку программы отбора проб'
              style={{ backgroundColor: 'green' }}
              onClick={handleOpenNextStage}
              loading={isUpdatingStage}
              disabled={!stage3Data?.groups || stage3Data.groups.length === 0 || isUpdatingStage}
            />
          </div>
        </Card>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        style={{ width: '80%', maxWidth: '1200px' }}
      >
        <div style={{ padding: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <Text size="2xl" weight="bold">Настройка группировки скважин</Text>
            <Button
              label="Добавить строку"
              iconLeft={IconAdd}
              onClick={addNewRow}
              size="s"
              view="ghost"
            />
          </div>

          {isLoadingCategories ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '40px',
              minHeight: '200px'
            }}>
              <Loader />
            </div>
          ) : (
            <Table
              rows={groupingRows}
              //@ts-ignore
              columns={groupingColumns}
              size="s"
              borderBetweenRows
              style={{ marginBottom: '20px' }}
            />
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '20px'
          }}>
            <Button
              label="Отмена"
              view="ghost"
              onClick={handleCloseModal}
            />
            <Button
              label="Сохранить"
              view="primary"
              onClick={handleSaveGrouping}
              loading={isUpdatingStage}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

const Stage4 = ({ navigate, projectUuid, onOpenModal }: any) => {
  // Получение данных для этапа 4
  const [getStage4Data, { data: stage4Data, isLoading: isLoadingStage4 }] = useLazyGetProjectCurrentStageInfoQuery();

  // Состояние для редактируемых строк
  const [editableRows, setEditableRows] = useState<any[]>([]);

  // Мутация для сохранения изменений
  const [updateWell, { isLoading: isSaving }] = usePatchWellMutation();

  // Мутация для обновления проекта
  const [patchProject, { isLoading: isUpdatingStage }] = usePatchProjectMutation();

  // Мутация для импорта плотностей
  const [wellDensityImport, { isLoading: isImporting }] = useWellDensityImportMutation();

  // Хук для получения участков
  const [getAreas, { data: areasData, isLoading: isLoadingAreas }] = useLazyGetAreaQuery();

  // Состояние для групповых свитчей
  const [groupSwitches, setGroupSwitches] = useState({
    is_sample_taken: false,
    is_oil_sample_received: false,
    is_oil_split: false,
    is_water_sample_received: false,
    is_water_split: false,
  });

  // Состояния для группового заполнения
  const [groupDate, setGroupDate] = useState<Date | null>(null);
  const [groupOilVolume, setGroupOilVolume] = useState<string>('');
  const [groupWaterVolume, setGroupWaterVolume] = useState<string>('');

  // Состояние для модального окна с таблицей
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);

  // Состояния для группового заполнения в модальном окне
  const [modalGroupSwitches, setModalGroupSwitches] = useState({
    is_sample_taken: false,
    is_oil_sample_received: false,
    is_oil_split: false,
    is_water_sample_received: false,
    is_water_split: false,
  });
  const [modalGroupDate, setModalGroupDate] = useState<Date | null>(null);
  const [modalGroupOilVolume, setModalGroupOilVolume] = useState<string>('');
  const [modalGroupWaterVolume, setModalGroupWaterVolume] = useState<string>('');

  // Состояния для модального окна загрузки плотностей
  const [isDensityModalOpen, setIsDensityModalOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Состояния для выбора участка
  const [selectedArea, setSelectedArea] = useState<any>(null);
  const [areaItems, setAreaItems] = useState<any[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Функции для пагинации
  const getPaginatedData = (data: any[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const addIndexToData = (data: any[], page: number) => {
    return data.map((item, idx) => ({
      ...item,
      rowNumber: (page - 1) * itemsPerPage + idx + 1
    }));
  };

  // Получаем данные для отображения с пагинацией
  const paginatedRows = addIndexToData(getPaginatedData(editableRows, currentPage), currentPage);
  const modalPaginatedRows = addIndexToData(getPaginatedData(editableRows, modalCurrentPage), modalCurrentPage);
  const totalPages = Math.ceil(editableRows.length / itemsPerPage);

  // Обработчики смены страницы
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleModalPageChange = (newPage: number) => {
    setModalCurrentPage(newPage);
  };

  // Сбрасываем страницы при изменении данных
  useEffect(() => {
    setCurrentPage(1);
    setModalCurrentPage(1);
  }, [editableRows.length]);

  useEffect(() => {
    if (projectUuid) {
      getStage4Data({
        uuid: projectUuid,
        current_stage: 4
      });
    }
  }, [projectUuid, getStage4Data]);

  // Инициализация данных при получении
  useEffect(() => {
    if (stage4Data && Array.isArray(stage4Data)) {
      const transformedData = stage4Data.map((well: any) => ({
        id: well.uuid,
        uuid: well.uuid,
        number: `Скв. ${well.number}`,
        area: well.area,
        area_name: well.area_name,
        is_sample_taken: well.is_sample_taken || false,
        sampling_date: well.sampling_date || '',
        sample_note: well.sample_note || '',
        is_oil_sample_received: well.is_oil_sample_received || false,
        oil_volume: well.oil_volume?.toString() || '',
        is_oil_split: well.is_oil_split || false,
        is_water_sample_received: well.is_water_sample_received || false,
        water_volume: well.water_volume?.toString() || '',
        is_water_split: well.is_water_split || false,
        water_density: well.water_density?.toString() || '',
        water_ec: well.water_ec?.toString() || '',
      }));
      setEditableRows(transformedData);

      // Обновляем состояние групповых свитчей на основе данных
      updateGroupSwitches(transformedData);
    }
  }, [stage4Data]);

  // Загрузка участков при открытии модалки
  useEffect(() => {
    if (isDensityModalOpen && projectUuid) {
      loadAreas();
    }
  }, [isDensityModalOpen, projectUuid]);

  // Обработка данных участков
  useEffect(() => {
    if (areasData?.results) {
      const areas = areasData.results.map((area: any) => ({
        label: area.name || 'Неизвестный участок',
        id: area.uuid,
        ...area
      }));
      setAreaItems(areas);
    }
  }, [areasData]);

  const loadAreas = async () => {
    if (!projectUuid) return;

    try {
      await getAreas({
        limit: 100,
        offset: 0,
        ordering: 'name',
        project: [projectUuid],
      }).unwrap();
    } catch (error) {
      console.error('❌ Ошибка загрузки участков:', error);
    }
  };

  // Функция для обновления состояния групповых свитчей
  const updateGroupSwitches = (rows: any[]) => {
    if (!rows.length) return;

    const newGroupSwitches: any = { ...groupSwitches };
    const newModalGroupSwitches: any = { ...modalGroupSwitches };

    // Проверяем каждый столбец
    Object.keys(groupSwitches).forEach(key => {
      const allTrue = rows.every(row => row[key] === true);
      const allFalse = rows.every(row => row[key] === false);

      // Если все true - включаем, если все false - выключаем, иначе indeterminate
      newGroupSwitches[key] = allTrue;
      newModalGroupSwitches[key] = allTrue;
    });

    setGroupSwitches(newGroupSwitches);
    setModalGroupSwitches(newModalGroupSwitches);
  };

  // Функция для обновления строки
  const updateRow = (rowId: string, updates: any) => {
    setEditableRows(prevRows => {
      const newRows = prevRows.map(row =>
        row.id === rowId ? { ...row, ...updates } : row
      );

      // Обновляем групповые свитчи после изменения строк
      updateGroupSwitches(newRows);

      return newRows;
    });
  };

  // Функция для массового обновления всех свитчей в столбце
  const handleGroupSwitchChange = (field: string) => (e: any) => {
    const checked = e?.target?.checked ?? e?.checked ?? e;

    // Обновляем все строки
    setEditableRows(prevRows => {
      const newRows = prevRows.map(row => ({
        ...row,
        [field]: Boolean(checked)
      }));

      return newRows;
    });

    // Обновляем состояние группового свитча
    setGroupSwitches(prev => ({
      ...prev,
      [field]: Boolean(checked)
    }));
    setModalGroupSwitches(prev => ({
      ...prev,
      [field]: Boolean(checked)
    }));
  };

  // Обработчики изменений - исправленные
  const handleSwitchChange = (rowId: string, field: string) => (e: any) => {
    const checked = e?.target?.checked ?? e?.checked ?? e;
    updateRow(rowId, { [field]: Boolean(checked) });
  };

  const handleDateChange = (rowId: string, field: string) => (value: Date | null) => {
    if (!value) {
      updateRow(rowId, { [field]: '' });
      return;
    }

    // Получаем локальную дату без времени
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    const dateString = `${year}-${month}-${day}`;
    updateRow(rowId, { [field]: dateString });
  };

  const handleTextChange = (rowId: string, field: string) => (e: any) => {
    const value = e?.target?.value ?? e?.value ?? e;
    updateRow(rowId, { [field]: value || '' });
  };

  const handleNumberChange = (rowId: string, field: string) => (e: any) => {
    const value = e?.target?.value ?? e?.value ?? e;
    // Разрешаем только числа и точку для десятичных значений
    const numericValue = value ? value.toString().replace(/[^\d.]/g, '') : '';
    updateRow(rowId, { [field]: numericValue });
  };

  // Функция для применения групповых значений ко всем строкам
  const applyGroupValues = () => {
    // Применяем значения только к пустым полям
    setEditableRows(prevRows => {
      return prevRows.map(row => {
        const updates: any = {};

        // Применяем дату, если она задана и поле пустое
        if (groupDate && !row.sampling_date) {
          const year = groupDate.getFullYear();
          const month = String(groupDate.getMonth() + 1).padStart(2, '0');
          const day = String(groupDate.getDate()).padStart(2, '0');
          updates.sampling_date = `${year}-${month}-${day}`;
        }

        // Применяем объем нефти, если задан и поле пустое
        if (groupOilVolume !== '' && !row.oil_volume) {
          updates.oil_volume = groupOilVolume;
        }

        // Применяем объем воды, если задан и поле пустое
        if (groupWaterVolume !== '' && !row.water_volume) {
          updates.water_volume = groupWaterVolume;
        }

        return Object.keys(updates).length > 0 ? { ...row, ...updates } : row;
      });
    });

    // Сбрасываем групповые значения после применения
    resetGroupValues();
  };

  // Функция для применения групповых значений из модального окна
  const applyModalGroupValues = () => {
    // Применяем значения только к пустым полям
    setEditableRows(prevRows => {
      return prevRows.map(row => {
        const updates: any = {};

        // Применяем дату, если она задана и поле пустое
        if (modalGroupDate && !row.sampling_date) {
          const year = modalGroupDate.getFullYear();
          const month = String(modalGroupDate.getMonth() + 1).padStart(2, '0');
          const day = String(modalGroupDate.getDate()).padStart(2, '0');
          updates.sampling_date = `${year}-${month}-${day}`;
        }

        // Применяем объем нефти, если задан и поле пустое
        if (modalGroupOilVolume !== '' && !row.oil_volume) {
          updates.oil_volume = modalGroupOilVolume;
        }

        // Применяем объем воды, если задан и поле пустое
        if (modalGroupWaterVolume !== '' && !row.water_volume) {
          updates.water_volume = modalGroupWaterVolume;
        }

        return Object.keys(updates).length > 0 ? { ...row, ...updates } : row;
      });
    });

    // Сбрасываем групповые значения в модальном окне после применения
    resetModalGroupValues();
  };

  // Функция для сброса групповых значений
  const resetGroupValues = () => {
    setGroupDate(null);
    setGroupOilVolume('');
    setGroupWaterVolume('');
  };

  // Функция для сброса групповых значений в модальном окне
  const resetModalGroupValues = () => {
    setModalGroupDate(null);
    setModalGroupOilVolume('');
    setModalGroupWaterVolume('');
  };

  // Обработчики для групповых полей ввода
  const handleGroupDateChange = (value: Date | null) => {
    setGroupDate(value);
  };

  const handleGroupOilVolumeChange = (e: any) => {
    const value = e?.target?.value ?? e;
    const numericValue = value ? value.toString().replace(/[^\d.]/g, '') : '';
    setGroupOilVolume(numericValue);
  };

  const handleGroupWaterVolumeChange = (e: any) => {
    const value = e?.target?.value ?? e;
    const numericValue = value ? value.toString().replace(/[^\d.]/g, '') : '';
    setGroupWaterVolume(numericValue);
  };

  // Обработчики для групповых полей ввода в модальном окне
  const handleModalGroupDateChange = (value: Date | null) => {
    setModalGroupDate(value);
  };

  const handleModalGroupOilVolumeChange = (e: any) => {
    const value = e?.target?.value ?? e;
    const numericValue = value ? value.toString().replace(/[^\d.]/g, '') : '';
    setModalGroupOilVolume(numericValue);
  };

  const handleModalGroupWaterVolumeChange = (e: any) => {
    const value = e?.target?.value ?? e;
    const numericValue = value ? value.toString().replace(/[^\d.]/g, '') : '';
    setModalGroupWaterVolume(numericValue);
  };

  // Функция для массового обновления всех свитчей в столбце в модальном окне
  const handleModalGroupSwitchChange = (field: string) => (e: any) => {
    const checked = e?.target?.checked ?? e?.checked ?? e;

    // Обновляем все строки
    setEditableRows(prevRows => {
      const newRows = prevRows.map(row => ({
        ...row,
        [field]: Boolean(checked)
      }));

      return newRows;
    });

    // Обновляем состояние группового свитча
    setModalGroupSwitches(prev => ({
      ...prev,
      [field]: Boolean(checked)
    }));
    setGroupSwitches(prev => ({
      ...prev,
      [field]: Boolean(checked)
    }));
  };

  // Обработчики для модального окна загрузки плотностей
  const handleOpenDensityModal = () => {
    setIsDensityModalOpen(true);
    // Сбрасываем состояния при открытии
    setUploadedFile(null);
    setSelectedArea(null);
    setImportError(null);
    setImportSuccess(false);
    setUploadProgress(0);
    setIsDragOver(false);
  };

  const handleCloseDensityModal = () => {
    setIsDensityModalOpen(false);
    setUploadedFile(null);
    setSelectedArea(null);
    setImportError(null);
    setImportSuccess(false);
    setUploadProgress(0);
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Обработчик загрузки файла
  const handleFileUpload = (file: File) => {
    setUploadedFile(file);
    setImportError(null);
    setImportSuccess(false);
  };

  // Обработчик удаления файла
  const handleFileRemove = () => {
    setUploadedFile(null);
    setImportError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Функция импорта плотностей
  const handleImportDensity = async () => {
    if (!uploadedFile) {
      setImportError('Пожалуйста, выберите файл для загрузки');
      return;
    }

    if (!selectedArea) {
      setImportError('Пожалуйста, выберите участок');
      return;
    }

    setImportError(null);
    setImportSuccess(false);
    setUploadProgress(10);

    try {
      // Имитация прогресса загрузки
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      await wellDensityImport({
        file: uploadedFile,
        area_uuid: selectedArea.uuid  // Используем ID выбранного участка
      }).unwrap();

      clearInterval(progressInterval);
      setUploadProgress(100);
      setImportSuccess(true);

      // Обновляем данные таблицы после успешной загрузки
      setTimeout(() => {
        getStage4Data({
          uuid: projectUuid,
          current_stage: 4
        });
      }, 1000);

      // Закрываем модальное окно через 2 секунды после успешной загрузки
      setTimeout(() => {
        handleCloseDensityModal();
      }, 2000);

    } catch (error: any) {
      console.error('Ошибка при импорте плотностей:', error);
      setImportError(error?.data?.message || 'Произошла ошибка при загрузке файла');
      setUploadProgress(0);
    }
  };

  // Функция сохранения данных с использованием usePatchWellMutation
  const handleSave = async () => {
    if (!editableRows.length) {
      return;
    }

    try {
      const savePromises = editableRows.map(async (row) => {
        // Преобразуем данные в правильный формат
        const updateData = {
          uuid: row.uuid,
          is_sample_taken: Boolean(row.is_sample_taken),
          sampling_date: row.sampling_date || null,
          sample_note: String(row.sample_note || ''),
          is_oil_sample_received: Boolean(row.is_oil_sample_received),
          oil_volume: parseFloat(row.oil_volume) || '',
          is_oil_split: Boolean(row.is_oil_split),
          is_water_sample_received: Boolean(row.is_water_sample_received),
          water_volume: parseFloat(row.water_volume) || '',
          is_water_split: Boolean(row.is_water_split),
          water_density: parseFloat(row.water_density) || 0,
          water_ec: parseFloat(row.water_ec) || 0,
        };

        try {
          const result = await updateWell(updateData).unwrap();
          return { success: true, well: row.number };
        } catch (error) {
          console.error(`❌ Ошибка сохранения скважины ${row.number}:`, error);
          return { success: false, well: row.number, error };
        }
      });

      const results = await Promise.all(savePromises);
      const successfulSaves = results.filter(r => r.success).length;
      const failedSaves = results.filter(r => !r.success).length;

      // Перезагрузить данные для обновления состояния
      if (successfulSaves > 0) {
        getStage4Data({
          uuid: projectUuid,
          current_stage: 4
        });
      }

    } catch (error) {
      console.error('💥 Общая ошибка при сохранении данных:', error);
    }
  };

  // Функция для перехода на следующий этап с обновлением current_stage
  const handleOpenNextStage = async () => {
    try {
      // Сначала сохраняем данные
      await handleSave();

      // Затем обновляем current_stage проекта
      if (projectUuid) {
        await patchProject({
          uuid: projectUuid,
          current_stage: 5,
        }).unwrap();
      }

      // Переходим на следующий этап
      navigate(`/project/${projectUuid}/monitoring/5`);

    } catch (error) {
      console.error('❌ Ошибка при переходе на следующий этап:', error);
    }
  };

  // Добавляем обработчик открытия модального окна с таблицей
  const handleOpenTableModal = () => {
    setIsTableModalOpen(true);
  };

  // Добавляем обработчик закрытия модального окна с таблицей
  const handleCloseTableModal = () => {
    setIsTableModalOpen(false);
  };

  // Колонки таблицы с исправленными обработчиками и групповыми свитчами
  const columnsMonitoring4 = [
    {
      title: '№',
      accessor: 'rowNumber',
      width: 60,
      renderCell: (row: any) => {
        return (
          <div>
            {row.rowNumber}
          </div>
        );
      }
    },
    {
      title: '№ скважины',
      accessor: 'number',
      renderCell: (row: any) => (
        <Text>{row.number}</Text>
      )
    },
    {
      title: <Text style={{ width: '350px', overflow: 'none' }}>Сверка выполнения программы отбора проб</Text>,
      columns: [
        {
          title: '',
          columns: [
            {
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Switch
                    size='s'
                    checked={groupSwitches.is_sample_taken}
                    onChange={handleGroupSwitchChange('is_sample_taken')}
                  />
                  <Text>Проба отобрана</Text>
                </div>
              ),
              accessor: 'is_sample_taken',
              renderCell: (row: any) => (
                <Switch
                  size='s'
                  checked={row.is_sample_taken}
                  onChange={handleSwitchChange(row.id, 'is_sample_taken')}
                />
              )
            },
            {
              title: 'Дата отбора',
              accessor: 'sampling_date',
              renderCell: (row: any) => {
                // Парсим дату с учетом часового пояса
                const getDateValue = (dateString: string) => {
                  if (!dateString) return null;

                  // Разделяем дату на части
                  const parts = dateString.split('-');
                  if (parts.length !== 3) return null;

                  const [year, month, day] = parts.map(Number);

                  // Создаем дату в локальном часовом поясе
                  return new Date(year, month - 1, day);
                };

                return (
                  <DatePicker
                    size='s'
                    value={getDateValue(row.sampling_date)}
                    onChange={handleDateChange(row.id, 'sampling_date')}
                  />
                );
              }
            },
            {
              title: 'Примечание для пробы',
              accessor: 'sample_note',
              renderCell: (row: any) => (
                <TextField
                  placeholder='Примечание'
                  size='s'
                  value={row.sample_note}
                  onChange={handleTextChange(row.id, 'sample_note')}
                />
              )
            },
          ]
        }
      ],
    },
    {
      title: 'Сверка выполнения программы отбора проб',
      columns: [
        {
          title: 'Нефть',
          columns: [
            {
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Switch
                    size='s'
                    checked={groupSwitches.is_oil_sample_received}
                    onChange={handleGroupSwitchChange('is_oil_sample_received')}
                  />
                  <Text>Образец</Text>
                </div>
              ),
              accessor: 'is_oil_sample_received',
              renderCell: (row: any) => (
                <Switch
                  size='s'
                  checked={row.is_oil_sample_received}
                  onChange={handleSwitchChange(row.id, 'is_oil_sample_received')}
                />
              )
            },
            {
              title: 'Объем, мл',
              accessor: 'oil_volume',
              renderCell: (row: any) => (
                <TextField
                  placeholder='мл'
                  size='s'
                  value={row.oil_volume}
                  onChange={handleNumberChange(row.id, 'oil_volume')}
                />
              )
            },
            {
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Switch
                    size='s'
                    checked={groupSwitches.is_oil_split}
                    onChange={handleGroupSwitchChange('is_oil_split')}
                  />
                  <Text>Разлито</Text>
                </div>
              ),
              accessor: 'is_oil_split',
              renderCell: (row: any) => (
                <Switch
                  size='s'
                  checked={row.is_oil_split}
                  onChange={handleSwitchChange(row.id, 'is_oil_split')}
                />
              )
            },
          ]
        },
        {
          title: 'Вода',
          columns: [
            {
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Switch
                    size='s'
                    checked={groupSwitches.is_water_sample_received}
                    onChange={handleGroupSwitchChange('is_water_sample_received')}
                  />
                  <Text>Образец</Text>
                </div>
              ),
              accessor: 'is_water_sample_received',
              renderCell: (row: any) => (
                <Switch
                  size='s'
                  checked={row.is_water_sample_received}
                  onChange={handleSwitchChange(row.id, 'is_water_sample_received')}
                />
              )
            },
            {
              title: 'Объем, мл',
              accessor: 'water_volume',
              renderCell: (row: any) => (
                <TextField
                  placeholder='мл'
                  size='s'
                  value={row.water_volume}
                  onChange={handleNumberChange(row.id, 'water_volume')}
                />
              )
            },
            {
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Switch
                    size='s'
                    checked={groupSwitches.is_water_split}
                    onChange={handleGroupSwitchChange('is_water_split')}
                  />
                  <Text>Отфильтровано</Text>
                </div>
              ),
              accessor: 'is_water_split',
              renderCell: (row: any) => (
                <Switch
                  size='s'
                  checked={row.is_water_split}
                  onChange={handleSwitchChange(row.id, 'is_water_split')}
                />
              )
            },
          ]
        },
      ],
    },
    {
      title: 'Измерение воды (первичные)',
      columns: [
        {
          title: '',
          columns: [
            {
              title: 'Плотность',
              accessor: 'water_density',
              renderCell: (row: any) => (
                <TextField
                  placeholder='плотн.'
                  size='s'
                  value={row.water_density}
                  onChange={handleNumberChange(row.id, 'water_density')}
                />
              )
            },
            {
              title: 'УЭП',
              accessor: 'water_ec',
              renderCell: (row: any) => (
                <TextField
                  placeholder='мкС'
                  size='s'
                  value={row.water_ec}
                  onChange={handleNumberChange(row.id, 'water_ec')}
                />
              )
            },
          ]
        },
      ],
    },
  ];

  return (
    <>
      <div className={classes.header}>
        <Text size='2xl' weight='bold'>Мониторинг проекта - Этап 4</Text>
        <Text size='s' view='secondary'>Сверка выполнения, пробоподготовка и измерения воды</Text>
        {isLoadingStage4 && <Text size='s' view='secondary'>Загрузка данных...</Text>}
        {isSaving && <Text size='s' view='secondary'>Сохранение изменений...</Text>}
      </div>

      <div className={classes.cardMonitoring}>
        <Card className={classes.card}>
          <div className={classes.headerCard}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Text size='xl' weight='bold'>Сверка выполнения и измерения</Text>
              <Button
                label='Загрузка плотностей'
                size='s'
                onClick={handleOpenDensityModal}
                loading={isImporting}
              />
            </div>
            {editableRows.length > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                width: '100%',
                marginTop: '16px'
              }}>
                <Text size='s' weight='medium' style={{ marginBottom: '4px' }}>
                  Групповое заполнение (только для пустых полей):
                </Text>

                <div style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-end',
                  flexWrap: 'wrap'
                }}>
                  {/* Групповой выбор даты */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '200px' }}>
                    <Text size='xs' view='secondary'>Дата отбора для всех скважин:</Text>
                    <DatePicker
                      size='s'
                      value={groupDate}
                      onChange={handleGroupDateChange}
                      placeholder="Выберите дату"
                    />
                  </div>

                  {/* Групповой ввод объема нефти */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '150px' }}>
                    <Text size='xs' view='secondary'>Объем нефти для всех скважин (мл):</Text>
                    <TextField
                      placeholder="Объем нефти"
                      size='s'
                      value={groupOilVolume}
                      onChange={handleGroupOilVolumeChange}
                    />
                  </div>

                  {/* Групповой ввод объема воды */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '150px' }}>
                    <Text size='xs' view='secondary'>Объем воды для всех скважин (мл):</Text>
                    <TextField
                      placeholder="Объем воды"
                      size='s'
                      value={groupWaterVolume}
                      onChange={handleGroupWaterVolumeChange}
                    />
                  </div>

                  {/* Кнопки управления */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-end',
                    marginLeft: 'auto'
                  }}>
                    {/* Добавляем кнопку расширения */}
                    <Button
                      size='s'
                      iconLeft={IconExpand}
                      onClick={handleOpenTableModal}
                      view='ghost'
                      title="Открыть в модальном окне"
                    />
                    <Button
                      size='s'
                      view='secondary'
                      label="Сбросить"
                      onClick={resetGroupValues}
                      disabled={!groupDate && !groupOilVolume && !groupWaterVolume}
                    />
                    <Button
                      size='s'
                      label="Применить"
                      onClick={applyGroupValues}
                      disabled={!groupDate && !groupOilVolume && !groupWaterVolume}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={classes.centerCard}>
            {isLoadingStage4 ? (
              <div className={classes.loadingState}>
                <Text>Загрузка данных сверки...</Text>
              </div>
            ) : editableRows.length > 0 ? (
              <>
                <div className={classes.tableContainer} style={{ height: '45vh' }}>
                  <Table
                    className={classes.monitoringTable}
                    rows={paginatedRows} // Используем пагинированные данные
                    stickyColumns={1 && 2}
                    stickyHeader
                    size='s'
                    columns={columnsMonitoring4}
                    borderBetweenRows
                  />
                </div>

                {totalPages > 1 && (
                  <div className={classes.paginationSection} style={{
                    marginTop: '20px',
                    marginBottom: '10px',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Pagination
                      items={totalPages}
                      value={currentPage}
                      onChange={handlePageChange}
                      showFirstPage
                      showLastPage
                      visibleCount={7}
                      size='s'
                    />
                  </div>
                )}
              </>
            ) : (
              <div className={classes.emptyState}>
                <Text view="secondary">Нет данных для отображения</Text>
              </div>
            )}
          </div>

          <div className={classes.footerCard}>
            <Button
              label='Назад'
              view='ghost'
              onClick={() => navigate(`/project/${projectUuid}/monitoring/3`)}
            />
            <Button
              label='Сформировать мини-отчет'
              style={{ backgroundColor: 'purple' }}
              onClick={() => onOpenModal('stage4')}
              disabled={editableRows.length === 0}
            />
            <Button
              label={isSaving ? "Сохранение..." : "Сохранить"}
              style={{ backgroundColor: 'green' }}
              onClick={handleSave}
              loading={isSaving}
              disabled={editableRows.length === 0}
            />
            <Button
              label='Задание на исследование'
              style={{ backgroundColor: 'green' }}
              onClick={handleOpenNextStage}
              loading={isUpdatingStage}
              disabled={editableRows.length === 0 || isUpdatingStage}
            />
          </div>
        </Card>
      </div>

      {/* Модальное окно загрузки плотностей */}
      <Modal
        isOpen={isDensityModalOpen}
        onClose={handleCloseDensityModal}
        hasOverlay
        onClickOutside={handleCloseDensityModal}
        onEsc={handleCloseDensityModal}
      >
        <div style={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          minWidth: '500px',
          maxWidth: '600px'
        }}>
          {/* Заголовок */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Text size='xl' weight='bold'>Загрузка плотностей</Text>
            <Button
              size='s'
              iconLeft={IconClose}
              view='clear'
              onClick={handleCloseDensityModal}
              disabled={isImporting}
            />
          </div>

          {/* Информационный текст */}
          <div>
            <Text size='s' view='secondary'>
              Загрузите файл с данными плотностей. Поддерживаемые форматы: .xlsx, .xls, .csv
            </Text>
          </div>

          {/* Селектор выбора участка */}
          <div>
            <Combobox
              label='Участок'
              required
              items={areaItems}
              value={selectedArea}
              onChange={setSelectedArea}
              placeholder={isLoadingAreas ? "Загрузка участков..." : "Выберите участок"}
              disabled={isLoadingAreas || isImporting}
            />
          </div>

          {/* Область drag&drop */}
          <div>
            <Text size='s' view='secondary' style={{ marginBottom: '8px' }} required>
              Файл с данными плотностей *
            </Text>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);

                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  const file = files[0];
                  const allowedExtensions = ['.xlsx', '.xls', '.csv'];
                  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

                  if (fileExtension && allowedExtensions.includes(fileExtension)) {
                    setUploadedFile(file);
                    setImportError(null);
                  } else {
                    setImportError('Неподдерживаемый формат файла. Используйте .xlsx, .xls или .csv');
                  }
                }
              }}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragOver ? '#0070f0' : '#e0e0e0'}`,
                borderRadius: '8px',
                padding: '32px 20px',
                textAlign: 'center',
                backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
                cursor: isImporting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isImporting ? 0.6 : 1,
              }}
            >
              {uploadedFile ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text weight="semibold" style={{ wordBreak: 'break-word' }}>
                      {uploadedFile.name}
                    </Text>
                    <Text size="s" view="secondary">
                      Размер: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </div>
                  {!isImporting && (
                    <Button
                      size="s"
                      view="clear"
                      iconLeft={IconTrash}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileRemove();
                      }}
                      style={{ color: '#ff4d4f' }}
                      label='Удалить'
                    />
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  {/*@ts-ignore */}
                  <IconUpload size="m" view="secondary" />
                  <div style={{ textAlign: 'center' }}>
                    <Text weight="semibold">Перетащите файл сюда</Text>
                    <Text size="s" view="secondary">или нажмите для выбора</Text>
                  </div>
                  <Text size="s" view="secondary">
                    Поддерживаемые форматы: .xlsx, .xls, .csv
                  </Text>
                </div>
              )}
            </div>

            {/* Скрытый input для файлов */}
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  const file = files[0];
                  const allowedExtensions = ['.xlsx', '.xls', '.csv'];
                  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

                  if (fileExtension && allowedExtensions.includes(fileExtension)) {
                    setUploadedFile(file);
                    setImportError(null);
                  } else {
                    setImportError('Неподдерживаемый формат файла. Используйте .xlsx, .xls или .csv');
                  }
                }
              }}
              accept=".xlsx,.xls,.csv"
              disabled={isImporting}
            />
          </div>

          {/* Отображение ошибки */}
          {importError && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fee',
              borderRadius: '4px',
              color: '#c00'
            }}>
              <Text size="s" style={{ color: '#c00' }}>{importError}</Text>
            </div>
          )}

          {/* Отображение успеха */}
          {importSuccess && (
            <div style={{
              padding: '12px',
              backgroundColor: '#e6ffe6',
              borderRadius: '4px',
              color: '#0a0'
            }}>
              <Text size="s" style={{ color: '#0a0' }}>Файл успешно загружен! Данные обновляются...</Text>
            </div>
          )}

          {/* Прогресс бар */}
          {isImporting && (
            <div>
              <Text size="s" view="secondary" style={{ marginBottom: '8px' }}>
                Загрузка: {uploadProgress}%
              </Text>
              <div
                style={{
                  width: '100%',
                  height: '6px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    backgroundColor: '#0070f0',
                    transition: 'width 0.3s ease',
                    borderRadius: '3px'
                  }}
                />
              </div>
            </div>
          )}

          {/* Кнопки действий */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '16px'
          }}>
            <Button
              label="Отмена"
              view="ghost"
              onClick={handleCloseDensityModal}
              disabled={isImporting}
            />
            <Button
              label={isImporting ? "Загрузка..." : "Загрузить"}
              onClick={handleImportDensity}
              disabled={!uploadedFile || !selectedArea || isImporting || importSuccess}
              loading={isImporting}
            />
          </div>
        </div>
      </Modal>

      {/* Модальное окно с таблицей */}
      <Modal
        isOpen={isTableModalOpen}
        onClose={handleCloseTableModal}
        hasOverlay
        className={classes.modalFullScreen}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', height: '82vh' }}>
          {editableRows.length > 0 && (
            <div>
              <Text size='s' weight='medium' style={{ marginBottom: '12px' }}>
                Групповое заполнение (только для пустых полей):
              </Text>

              <div style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
                justifyContent: 'space-between'
              }}>
                {/* Групповой выбор даты */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  flexDirection: 'row'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '200px' }}>
                    <Text size='xs' view='secondary'>Дата отбора для всех скважин:</Text>
                    <DatePicker
                      size='s'
                      value={modalGroupDate}
                      onChange={handleModalGroupDateChange}
                      placeholder="Выберите дату"
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '200px' }}>
                    <Text size='xs' view='secondary'>Объем нефти для всех скважин (мл):</Text>
                    <TextField
                      placeholder="Объем нефти"
                      size='s'
                      value={modalGroupOilVolume}
                      onChange={handleModalGroupOilVolumeChange}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '200px' }}>
                    <Text size='xs' view='secondary'>Объем воды для всех скважин (мл):</Text>
                    <TextField
                      placeholder="Объем воды"
                      size='s'
                      value={modalGroupWaterVolume}
                      onChange={handleModalGroupWaterVolumeChange}
                    />
                  </div>
                </div>

                {/* Кнопки управления в модальном окне */}
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                  <Button
                    size='s'
                    view='secondary'
                    label="Сбросить"
                    onClick={resetModalGroupValues}
                    disabled={!modalGroupDate && !modalGroupOilVolume && !modalGroupWaterVolume}
                  />
                  <Button
                    size='s'
                    label="Применить"
                    onClick={applyModalGroupValues}
                    disabled={!modalGroupDate && !modalGroupOilVolume && !modalGroupWaterVolume}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Таблица в модальном окне */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ overflow: 'auto', flex: 1 }}>
              <Table
                rows={modalPaginatedRows} // Используем пагинированные данные для модального окна
                stickyColumns={1}
                stickyHeader
                size='s'
                columns={columnsMonitoring4.map(col => {
                  // Обновляем заголовки свитчей для модального окна
                  if (col.accessor === 'is_sample_taken') {
                    return {
                      ...col,
                      title: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Switch
                            size='s'
                            checked={modalGroupSwitches.is_sample_taken}
                            onChange={handleModalGroupSwitchChange('is_sample_taken')}
                          />
                          <Text>Проба отобрана</Text>
                        </div>
                      )
                    };
                  } else if (col.accessor === 'is_oil_sample_received') {
                    return {
                      ...col,
                      title: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Switch
                            size='s'
                            checked={modalGroupSwitches.is_oil_sample_received}
                            onChange={handleModalGroupSwitchChange('is_oil_sample_received')}
                          />
                          <Text>Образец</Text>
                        </div>
                      )
                    };
                  } else if (col.accessor === 'is_oil_split') {
                    return {
                      ...col,
                      title: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Switch
                            size='s'
                            checked={modalGroupSwitches.is_oil_split}
                            onChange={handleModalGroupSwitchChange('is_oil_split')}
                          />
                          <Text>Разлито</Text>
                        </div>
                      )
                    };
                  } else if (col.accessor === 'is_water_sample_received') {
                    return {
                      ...col,
                      title: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Switch
                            size='s'
                            checked={modalGroupSwitches.is_water_sample_received}
                            onChange={handleModalGroupSwitchChange('is_water_sample_received')}
                          />
                          <Text>Образец</Text>
                        </div>
                      )
                    };
                  } else if (col.accessor === 'is_water_split') {
                    return {
                      ...col,
                      title: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Switch
                            size='s'
                            checked={modalGroupSwitches.is_water_split}
                            onChange={handleModalGroupSwitchChange('is_water_split')}
                          />
                          <Text>Отфильтровано</Text>
                        </div>
                      )
                    };
                  }
                  return col;
                })}
                borderBetweenRows
                //@ts-ignore
                getRowKey={(row: any) => row.id}
              />
            </div>

            {/* Пагинация для модального окна */}
            {totalPages > 1 && (
              <div className={classes.paginationSection} style={{
                marginTop: '20px',
                marginBottom: '10px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <Pagination
                  items={totalPages}
                  value={modalCurrentPage}
                  onChange={handleModalPageChange}
                  showFirstPage
                  showLastPage
                  visibleCount={7}
                  size='s'
                />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Button
              label='Закрыть'
              onClick={handleCloseTableModal}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

// Stage 5: Задание на исследование
const Stage5 = ({ navigate, projectUuid, onOpenModal }: any) => {
  // Получение данных для этапа 5
  const [getStage5Data, { data: stage5Data, isLoading: isLoadingStage5 }] = useLazyGetProjectCurrentStageInfoQuery();

  // Состояние для редактируемых строк
  const [editableRows, setEditableRows] = useState<any[]>([]);

  // Мутация для сохранения изменений
  const [updateWell, { isLoading: isSaving }] = usePatchWellMutation();

  // Мутация для завершения мониторинга
  const [patchSetCompleted, { isLoading: isCompleting }] = usePatchSetCompletedMutation();

  // Мутация для создания отчета
  const [postReport, { isLoading: isCreatingReport }] = usePostReportMutation();

  // Запрос для получения отчета по проекту
  const [getByProject, { isLoading: isLoadingReport }] = useLazyGetByProjectQuery();

  // Мутация для обновления проекта
  const [patchProject, { isLoading: isUpdatingStage }] = usePatchProjectMutation();

  // Состояние для групповых чекбоксов
  const [groupCheckboxes, setGroupCheckboxes] = useState({
    gh: false,
    icp: false,
    hab: false,
    capel: false,
  });

  // Статус групповых чекбоксов
  const [groupCheckboxStatus, setGroupCheckboxStatus] = useState({
    gh: { allEnabled: false, anyEnabled: false },
    icp: { allEnabled: false, anyEnabled: false },
    hab: { allEnabled: false, anyEnabled: false },
    capel: { allEnabled: false, anyEnabled: false },
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Функции для пагинации
  const getPaginatedData = (data: any[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const addIndexToData = (data: any[], page: number) => {
    return data.map((item, idx) => ({
      ...item,
      rowNumber: (page - 1) * itemsPerPage + idx + 1
    }));
  };

  // Получаем данные для отображения с пагинацией
  const paginatedRows = addIndexToData(getPaginatedData(editableRows, currentPage), currentPage);
  const totalPages = Math.ceil(editableRows.length / itemsPerPage);

  // Обработчик смены страницы
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Сбрасываем страницу при изменении данных
  useEffect(() => {
    setCurrentPage(1);
  }, [editableRows.length]);

  useEffect(() => {
    if (projectUuid) {
      getStage5Data({
        uuid: projectUuid,
        current_stage: 5
      });
    }
  }, [projectUuid, getStage5Data]);

  // Инициализация данных при получении
  useEffect(() => {
    if (stage5Data && Array.isArray(stage5Data)) {
      //('📊 Данные Stage5:', stage5Data);
      const transformedData = stage5Data.map((well: any) => ({
        id: well.uuid,
        uuid: well.uuid,
        name: `Скв. ${well.number}`,
        category: well.category_name || 'Не указана',
        plast: well.layers_info?.map((layer: any) => layer.name).join(', ') || 'Не указан',
        gh: well.is_gh || false,
        icp: well.is_icp || false,
        hab: well.is_hav || false,
        capel: well.is_kapel || false,
        is_oil_sample_received: well.is_oil_sample_received || false,
        is_water_sample_received: well.is_water_sample_received || false,
        water_volume: well.water_volume || 0,
        oil_volume: well.oil_volume || 0,
      }));
      setEditableRows(transformedData);

      // Обновляем состояние групповых чекбоксов
      updateGroupCheckboxes(transformedData);
    }
  }, [stage5Data]);

  // Функция для обновления состояния групповых чекбоксов
  const updateGroupCheckboxes = (rows: any[]) => {
    if (!rows.length) return;

    const newGroupCheckboxes: any = { ...groupCheckboxes };
    const newGroupCheckboxStatus: any = { ...groupCheckboxStatus };

    // Проверяем каждый столбец
    Object.keys(groupCheckboxes).forEach(key => {
      // Все активные строки (не заблокированные)
      const enabledRows = rows.filter(row => isCheckboxEnabled(row, key));
      const disabledRows = rows.filter(row => !isCheckboxEnabled(row, key));

      // Если есть хотя бы одна активная строка
      const anyEnabled = enabledRows.length > 0;
      // Если все активные строки отмечены
      const allEnabledChecked = enabledRows.length > 0 && enabledRows.every(row => row[key] === true);
      // Если все строки активны
      const allRowsEnabled = disabledRows.length === 0;

      newGroupCheckboxes[key] = allEnabledChecked;
      newGroupCheckboxStatus[key] = {
        allEnabled: allRowsEnabled,
        anyEnabled: anyEnabled
      };
    });

    setGroupCheckboxes(newGroupCheckboxes);
    setGroupCheckboxStatus(newGroupCheckboxStatus);
  };

  // Проверка доступности чекбокса для конкретной строки
  const isCheckboxEnabled = (row: any, field: string): boolean => {
    switch (field) {
      case 'gh':
        return row.is_oil_sample_received === true;
      case 'icp':
        return row.is_water_sample_received === true;
      case 'hab':
        return row.is_water_sample_received === true && row.water_volume >= 200;
      case 'capel':
        return row.is_water_sample_received === true;
      default:
        return true;
    }
  };

  // Функция для обновления строки
  const updateRow = (rowId: string, updates: any) => {
    setEditableRows(prevRows => {
      const newRows = prevRows.map(row =>
        row.id === rowId ? { ...row, ...updates } : row
      );

      // Обновляем групповые чекбоксы после изменения строк
      updateGroupCheckboxes(newRows);

      return newRows;
    });
  };

  // Функция для массового обновления всех чекбоксов в столбце
  const handleGroupCheckboxChange = (field: string) => (e: any) => {
    const checked = e?.target?.checked ?? e?.checked ?? e;

    //(`🔄 Group checkbox ${field} changed to:`, checked);

    // Обновляем все активные строки
    setEditableRows(prevRows => {
      const newRows = prevRows.map(row => {
        if (isCheckboxEnabled(row, field)) {
          return {
            ...row,
            [field]: Boolean(checked)
          };
        }
        return row;
      });

      return newRows;
    });

    // Обновляем состояние группового чекбокса
    setGroupCheckboxes(prev => ({
      ...prev,
      [field]: Boolean(checked)
    }));
  };

  // Обработчики изменений для чекбоксов
  const handleCheckboxChange = (rowId: string, field: string) => (e: any) => {
    const checked = e?.target?.checked ?? e?.checked ?? e;
    //(`✅ Checkbox ${field} changed:`, checked);
    updateRow(rowId, { [field]: Boolean(checked) });
  };

  // Функция сохранения данных
  const handleSave = async () => {
    if (!editableRows.length) {
      //('❌ Нет данных для сохранения');
      return;
    }

    //('💾 Начало сохранения данных Stage5');

    try {
      const savePromises = editableRows.map(async (row) => {
        const updateData = {
          uuid: row.uuid,
          is_gh: Boolean(row.gh),
          is_icp: Boolean(row.icp),
          is_hav: Boolean(row.hab),
          is_kapel: Boolean(row.capel),
        };

        //(`📤 Сохранение исследований для скважины ${row.name}:`, updateData);

        try {
          const result = await updateWell(updateData).unwrap();
          //(`✅ Исследования для скважины ${row.name} сохранены`);
          return { success: true, well: row.name };
        } catch (error) {
          console.error(`❌ Ошибка сохранения исследований для скважины ${row.name}:`, error);
          return { success: false, well: row.name, error };
        }
      });

      const results = await Promise.all(savePromises);
      const successfulSaves = results.filter(r => r.success).length;
      const failedSaves = results.filter(r => !r.success).length;

      //(`📊 Результаты сохранения исследований: ${successfulSaves} успешно, ${failedSaves} с ошибками`);

      if (successfulSaves > 0) {
        getStage5Data({
          uuid: projectUuid,
          current_stage: 5
        });
      }

    } catch (error) {
      console.error('💥 Общая ошибка при сохранении данных исследований:', error);
    }
  };

  // Функция для завершения мониторинга с созданием отчета и переходом
  const handleCompleteMonitoring = async () => {
    if (!projectUuid) {
      //('❌ Не указан projectUuid для завершения мониторинга');
      alert('Ошибка: не указан идентификатор проекта');
      return;
    }

    const isConfirmed = window.confirm(
      'Вы уверены, что хотите завершить мониторинг проекта? Будет создан итоговый отчет и внесение изменений будет ограничено.'
    );

    if (!isConfirmed) {
      return;
    }

    try {
      //('🏁 Начало завершения мониторинга для проекта:', projectUuid);

      // Шаг 1: Создаем итоговый отчет
      //('📊 Создание итогового отчета...');
      const reportResponse = await postReport({
        category: 'monitoring',
        project: projectUuid
      }).unwrap();

      //('✅ Итоговый отчет создан:', reportResponse);

      // Шаг 2: Завершаем мониторинг проекта
      //('✅ Завершение мониторинга проекта...');
      await patchSetCompleted({
        uuid: projectUuid
      }).unwrap();

      //('✅ Мониторинг успешно завершен');

      // Шаг 3: Обновляем статус проекта
      //('🔄 Обновление статуса проекта...');
      await patchProject({
        uuid: projectUuid,
        is_completed: true,
        current_stage: 5
      }).unwrap();

      // Шаг 4: Получаем отчет по проекту для получения UUID
      //('📋 Получение данных отчета по проекту...');
      const reportData = await getByProject({
        project_uuid: projectUuid
      }).unwrap();

      //('✅ Данные отчета получены:', reportData);

      // Шаг 5: Переходим на страницу отчета
      if (reportData && reportData.uuid) {
        //('🔗 Переход на страницу отчета:', `/report/${reportData.uuid}`);

        // Показываем сообщение об успехе
        alert('Мониторинг проекта успешно завершен! Открываю отчет...');

        // Переходим на страницу отчета
        navigate(`/report/${reportData.uuid}`);
      } else {
        console.error('❌ Не удалось получить UUID отчета');
        alert('Мониторинг завершен, но не удалось открыть отчет. Перейдите в раздел "Отчеты" вручную.');
        navigate('/report');
      }

    } catch (error: any) {
      console.error('❌ Ошибка при завершении мониторинга:', error);

      // Более детальное сообщение об ошибке
      let errorMessage = 'Произошла ошибка при завершении мониторинга. ';

      if (error?.status === 400) {
        errorMessage += 'Неверные данные запроса.';
      } else if (error?.status === 404) {
        errorMessage += 'Проект не найден.';
      } else if (error?.status === 500) {
        errorMessage += 'Внутренняя ошибка сервера.';
      } else {
        errorMessage += 'Пожалуйста, попробуйте еще раз.';
      }

      alert(errorMessage);
    }
  };

  // Общий статус загрузки для завершения
  const isCompletingProcess = isCompleting || isCreatingReport || isUpdatingStage || isLoadingReport;

  // Вспомогательная функция для отображения информации о доступности чекбокса
  const getCheckboxTooltip = (field: string): string => {
    switch (field) {
      case 'gh':
        return 'Доступен только если есть образец нефти';
      case 'icp':
        return 'Доступен только если есть образец воды';
      case 'hab':
        return 'Доступен только если есть образец воды и объем ≥ 200 мл';
      case 'capel':
        return 'Доступен только если есть образец воды';
      default:
        return '';
    }
  };

  // Колонки таблицы с групповыми чекбоксами в заголовках
  const columnsMonitoring5 = [
    {
      title: '№ скважины',
      accessor: 'name',
      sortable: true,
      renderCell: (row: any) => (
        <Text>{row.name}</Text>
      )
    },
    {
      title: 'Категория',
      accessor: 'category',
      sortable: true,
      renderCell: (row: any) => (
        <Text>{row.category}</Text>
      )
    },
    {
      title: 'Пласт',
      accessor: 'plast',
      width: 200,
      sortable: true,
      renderCell: (row: any) => (
        <Text>{row.plast}</Text>
      )
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Checkbox
            checked={groupCheckboxes.gh}
            onChange={handleGroupCheckboxChange('gh')}
            disabled={!groupCheckboxStatus.gh.anyEnabled}
          />
          <div>
            <Text>ГХ</Text>
          </div>
        </div>
      ),
      accessor: 'gh',
      renderCell: (row: any) => (
        <Checkbox
          checked={row.gh}
          onChange={handleCheckboxChange(row.id, 'gh')}
          disabled={row.is_oil_sample_received === false}
        />
      )
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Checkbox
            checked={groupCheckboxes.icp}
            onChange={handleGroupCheckboxChange('icp')}
            disabled={!groupCheckboxStatus.icp.anyEnabled}
          />
          <div>
            <Text>ICP</Text>
          </div>
        </div>
      ),
      accessor: 'icp',
      renderCell: (row: any) => (
        <Checkbox
          checked={row.icp}
          onChange={handleCheckboxChange(row.id, 'icp')}
          disabled={row.is_water_sample_received === false}
        />
      )
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Checkbox
            checked={groupCheckboxes.hab}
            onChange={handleGroupCheckboxChange('hab')}
            disabled={!groupCheckboxStatus.hab.anyEnabled}
          />
          <div>
            <Text>ХАВ</Text>
          </div>
        </div>
      ),
      accessor: 'hab',
      renderCell: (row: any) => (
        <Checkbox
          checked={row.hab}
          onChange={handleCheckboxChange(row.id, 'hab')}
          disabled={row.is_water_sample_received === false || row.water_volume < 200}
        />
      )
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Checkbox
            checked={groupCheckboxes.capel}
            onChange={handleGroupCheckboxChange('capel')}
            disabled={!groupCheckboxStatus.capel.anyEnabled}
          />
          <div>
            <Text>Капель</Text>
          </div>
        </div>
      ),
      accessor: 'capel',
      renderCell: (row: any) => (
        <Checkbox
          checked={row.capel}
          onChange={handleCheckboxChange(row.id, 'capel')}
          disabled={row.is_water_sample_received === false}
        />
      )
    },
  ];

  return (
    <>
      <div className={classes.header}>
        <Text size='2xl' weight='bold'>Мониторинг проекта - Этап 5</Text>
        <Text size='s' view='secondary'>Задание на исследование</Text>
        {isLoadingStage5 && <Text size='s' view='secondary'>Загрузка данных...</Text>}
        {isSaving && <Text size='s' view='secondary'>Сохранение изменений...</Text>}
        {isCompletingProcess && (
          <Text size='s' view='secondary'>
            {isCreatingReport && "Создание отчета... "}
            {isCompleting && "Завершение мониторинга... "}
            {isUpdatingStage && "Обновление проекта... "}
            {isLoadingReport && "Получение данных отчета..."}
          </Text>
        )}
      </div>

      <div className={classes.cardMonitoring}>
        <Card className={classes.card}>
          <div className={classes.headerCard}>
            <Text size='xl' weight='bold'>Формирование задания на исследование</Text>
            {editableRows.length > 0 && (
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <Text size='s' view='secondary'>
                  Всего скважин: {editableRows.length}
                </Text>
              </div>
            )}
          </div>

          <div className={classes.centerCard} style={{ overflowY: 'auto' }}>
            {isLoadingStage5 ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Text>Загрузка данных задания на исследование...</Text>
              </div>
            ) : editableRows.length > 0 ? (
              <>
                <div style={{ width: '100%' }}>
                  <Table
                    className={classes.tableContainer1}
                    rows={paginatedRows} // Используем пагинированные данные
                    stickyHeader
                    columns={columnsMonitoring5}
                    borderBetweenRows
                  />
                </div>

                {totalPages > 1 && (
                  <div className={classes.paginationSection} style={{
                    marginTop: '20px',
                    marginBottom: '10px',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Pagination
                      items={totalPages}
                      value={currentPage}
                      onChange={handlePageChange}
                      showFirstPage
                      showLastPage
                      visibleCount={7}
                      size='s'
                    />
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Text view="secondary">Нет данных для отображения</Text>
              </div>
            )}
          </div>

          <div className={classes.footerCard}>
            <Button
              label='Назад'
              view='ghost'
              onClick={() => navigate(`/project/${projectUuid}/monitoring/4`)}
              disabled={isSaving || isCompletingProcess}
            />
            <Button
              label='Сформировать мини-отчет'
              style={{ backgroundColor: 'purple' }}
              onClick={() => onOpenModal('stage5')}
              disabled={editableRows.length === 0 || isSaving || isCompletingProcess}
            />
            <Button
              label={isSaving ? "Сохранение..." : "Сохранить"}
              style={{ backgroundColor: 'green' }}
              onClick={handleSave}
              loading={isSaving}
              disabled={editableRows.length === 0 || isSaving || isCompletingProcess}
            />
            <Button
              label={isCompletingProcess ? "Завершение..." : "Завершить мониторинг"}
              onClick={handleCompleteMonitoring}
              loading={isCompletingProcess}
              disabled={isSaving || isCompletingProcess}
            />
          </div>
        </Card>
      </div>
    </>
  );
};

export default MonitoringStages;